import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { format, parse, startOfWeek, getDay, addDays, startOfMonth, endOfMonth, eachDayOfInterval, getWeek, getYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  List, Grid3X3, Printer, Download, Clock, User, MapPin, Trash2, FileText,
  Check, X, Mail, Phone, Edit, AlertCircle, Star, Camera, CheckCircle2, UserPlus, RefreshCw, RotateCcw, Briefcase,
  Users, ChevronDown, Info, HelpCircle, Umbrella, Building2, Ban
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { companyApi } from '../../lib/api';
import { exportPlanningPDF } from './planningExport';
import { getAssetUrl } from '../../lib/assetUrl';
import { PlanningGuideTour } from '../../components/GuideTour';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import useFabAction from '../../hooks/useFabAction';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import PlanningHowItWorks from '../../components/PlanningHowItWorks';

// Jours fériés français (calculés pour l'année donnée)
const getFrenchHolidays = (year) => {
  // Jours fériés fixes
  const holidays = [
    { date: `${year}-01-01`, name: 'Jour de l\'an' },
    { date: `${year}-05-01`, name: 'Fête du Travail' },
    { date: `${year}-05-08`, name: 'Victoire 1945' },
    { date: `${year}-07-14`, name: 'Fête Nationale' },
    { date: `${year}-08-15`, name: 'Assomption' },
    { date: `${year}-11-01`, name: 'Toussaint' },
    { date: `${year}-11-11`, name: 'Armistice 1918' },
    { date: `${year}-12-25`, name: 'Noël' },
  ];
  
  // Calcul de Pâques (algorithme de Meeus/Jones/Butcher)
  try {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    
    const easter = new Date(year, month - 1, day);
    
    // Jours fériés basés sur Pâques
    const easterMonday = addDays(easter, 1);
    const ascension = addDays(easter, 39);
    const whitMonday = addDays(easter, 50);
    
    holidays.push(
      { date: format(easterMonday, 'yyyy-MM-dd'), name: 'Lundi de Pâques' },
      { date: format(ascension, 'yyyy-MM-dd'), name: 'Ascension' },
      { date: format(whitMonday, 'yyyy-MM-dd'), name: 'Lundi de Pentecôte' }
    );
  } catch (e) {
    console.warn('Error calculating Easter-based holidays:', e);
  }
  
  return holidays;
};

// Localizer for French
const locales = { 'fr': fr };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Wrap react-big-calendar with the drag & drop HOC so events can be
// moved (simple drag) or duplicated (Alt + drag) and resized directly
// on the calendar grid.
const DnDCalendar = withDragAndDrop(Calendar);

// Custom messages in French
const messages = {
  allDay: 'Journée',
  previous: 'Précédent',
  next: 'Suivant',
  today: "Aujourd'hui",
  month: 'Mois',
  week: 'Semaine',
  day: 'Jour',
  agenda: 'Agenda',
  date: 'Date',
  time: 'Heure',
  event: 'Événement',
  noEventsInRange: 'Aucun événement dans cette période',
  showMore: (total) => `+ ${total} autres`,
};

export default function AdvancedPlanningPage() {
  const [view, setView] = useState('week'); // Vue semaine par défaut
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [punctualBookings, setPunctualBookings] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]); // RDV commerciaux
  const [collaborators, setCollaborators] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateTargetDate, setDuplicateTargetDate] = useState('');
  const [newAvail, setNewAvail] = useState({ date: '', slots: [], collaborator_id: '' });
  const [bookingForm, setBookingForm] = useState({ 
    client_id: '', date: '', slot_type: 'half_day', reason: '', collaborator_ids: [], // Changed to array for multiple collaborators
    // Mode RDV (commercial)
    time_slot: '09:00',
    duration_minutes: 60,
    meeting_type: 'physical',
  });
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState('all');
  const [viewMode, setViewMode] = useState('members'); // 'calendar', 'team', or 'members' - Vue membres par défaut
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ 
    date: '', 
    slot_type: '', 
    slot_period: '',
    collaborator_id: '', 
    collaborator_ids: [],
    client_id: '',
    notes: '',
    reschedule_reason: '',
    reschedule_reason_custom: ''
  });
  
  // Predefined reschedule reasons
  const RESCHEDULE_REASONS = [
    { value: 'weather', label: 'Météo défavorable' },
    { value: 'client_unavailable', label: 'Indisponibilité client' },
    { value: 'collaborator_unavailable', label: 'Indisponibilité collaborateur' },
    { value: 'client_request', label: 'Demande du client' },
    { value: 'company_request', label: 'Décision entreprise' },
    { value: 'emergency', label: 'Urgence' },
    { value: 'other', label: 'Autre (préciser)' }
  ];
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [closures, setClosures] = useState([]); // Fermetures exceptionnelles
  const [collapsedMembers, setCollapsedMembers] = useState({}); // Track collapsed member rows
  const [workHours, setWorkHours] = useState([]); // Company work hours
  const [collaboratorLeaves, setCollaboratorLeaves] = useState([]); // Congés collaborateurs
  const [draggedEvent, setDraggedEvent] = useState(null); // Event being dragged
  const [dropTarget, setDropTarget] = useState(null); // Current drop target
  // Holds the latest 409 from the server when a drop would create a conflict.
  // Rendered as a modal that lists the unavailable collab(s) + next available
  // slots so the admin can pick an alternative without manual hunting.
  const [moveConflict, setMoveConflict] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false); // Complete with photo dialog
  const [showStartDialog, setShowStartDialog] = useState(false); // Start with photo dialog
  const [completePhoto, setCompletePhoto] = useState(null); // Photo for completion (after)
  const [startPhoto, setStartPhoto] = useState(null); // Photo for start (before)
  const [photoPreview, setPhotoPreview] = useState(null); // Photo preview URL
  const [startPhotoPreview, setStartPhotoPreview] = useState(null); // Start photo preview URL
  const [isQuickClientDialogOpen, setIsQuickClientDialogOpen] = useState(false); // Quick client creation
  const [quickClientForm, setQuickClientForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [slotTypes, setSlotTypes] = useState([]); // Types de créneaux personnalisés
  const printRef = useRef();
  const photoInputRef = useRef(null);
  const startPhotoInputRef = useRef(null);
  // Tracks whether the Alt key is currently pressed so calendar drag can
  // switch to "duplicate" mode (Alt + drag). react-big-calendar's
  // onEventDrop callback doesn't forward the DOM event, hence this ref.
  const altKeyRef = useRef(false);

  useEffect(() => {
    const setAlt = (e) => { altKeyRef.current = e.altKey; };
    const clearAlt = () => { altKeyRef.current = false; };
    window.addEventListener('keydown', setAlt);
    window.addEventListener('keyup', setAlt);
    window.addEventListener('blur', clearAlt);
    return () => {
      window.removeEventListener('keydown', setAlt);
      window.removeEventListener('keyup', setAlt);
      window.removeEventListener('blur', clearAlt);
    };
  }, []);

  // Open the "Réserver pour un client" dialog when redirected from FAB
  useFabAction({ 'new-booking': () => setIsBookingDialogOpen(true) });

  // Jours fériés pour l'année courante et suivante (calculés une fois)
  const holidays = useMemo(() => {
    const currentYear = getYear(currentDate);
    return [...getFrenchHolidays(currentYear), ...getFrenchHolidays(currentYear + 1)];
  }, [currentDate]);
  
  // Vérifier si une date est un jour férié
  const getHolidayForDate = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.find(h => h.date === dateStr);
  }, [holidays]);
  
  // Vérifier si une date est une fermeture exceptionnelle
  const getClosureForDate = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return closures.find(c => c.date === dateStr);
  }, [closures]);

  // Vérifier si un collaborateur est en congés pour une date donnée
  const getCollaboratorLeaveForDate = useCallback((collaboratorId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return collaboratorLeaves.find(leave => {
      if (leave.collaborator_id !== collaboratorId) return false;
      return dateStr >= leave.start_date && dateStr <= leave.end_date;
    });
  }, [collaboratorLeaves]);

  // Get all leaves for a collaborator
  const getCollaboratorAllLeaves = useCallback((collaboratorId) => {
    return collaboratorLeaves.filter(l => l.collaborator_id === collaboratorId);
  }, [collaboratorLeaves]);

  // For the edit modal — surface each collaborator's status for the date /
  // slot period currently being edited. This lets the admin see at a glance
  // who's free BEFORE hitting "Enregistrer" (which used to trigger a 409
  // conflict dialogue). Returns { status: 'available'|'leave'|'busy'|'self', reason }.
  const getCollabAvailabilityForEdit = useCallback((collabId, dateStr, slotPeriod, currentBookingId) => {
    if (!collabId || !dateStr) return { status: 'available' };

    // Rule 1 — Company closure/holiday overrides everything.
    const closureHit = closures.find(cl => dateStr >= cl.date && dateStr <= (cl.end_date || cl.date));
    if (closureHit && (closureHit.blocks_public_widgets !== false)) {
      return { status: 'closed', reason: closureHit.reason || 'Fermeture' };
    }

    // Rule 2 — Approved leave.
    const leave = collaboratorLeaves.find(l =>
      l.collaborator_id === collabId
      && dateStr >= l.start_date
      && dateStr <= l.end_date
    );
    if (leave) {
      return { status: 'leave', reason: `Congés du ${leave.start_date} au ${leave.end_date}` };
    }

    // Rule 3 — Already assigned to another booking that overlaps on the same
    // slot period (morning / afternoon / full_day). We exclude the booking
    // being edited (currentBookingId) so its own collab shows as 'self'.
    // When the caller didn't provide a slot_period (e.g. legacy bookings
    // relying only on slot_type), fall back to checking BOTH halves so we
    // don't silently miss overlaps.
    const isFullDay = slotPeriod === 'full_day';
    const normalisedPeriod = ['morning', 'afternoon', 'full_day'].includes(slotPeriod) ? slotPeriod : null;
    const conflictsWith = (b) => {
      if (!b || b.date !== dateStr) return false;
      if (b.id === currentBookingId) return false;
      if (['cancelled', 'completed'].includes(b.status)) return false;
      const collabList = b.collaborator_ids && b.collaborator_ids.length
        ? b.collaborator_ids
        : (b.collaborator_id ? [b.collaborator_id] : []);
      if (!collabList.includes(collabId)) return false;
      const bPeriod = b.slot_period;
      if (isFullDay || bPeriod === 'full_day') return true;
      if (!normalisedPeriod) return true; // Unknown target period → conservative
      return bPeriod === normalisedPeriod;
    };
    const busySub = bookings.find(conflictsWith);
    const busyPct = punctualBookings.find(conflictsWith);
    if (busySub || busyPct) {
      return {
        status: 'busy',
        reason: 'Déjà réservé sur ce créneau',
      };
    }

    return { status: 'available' };
  }, [collaboratorLeaves, closures, bookings, punctualBookings]);

  // ↺ Highlight on event cards: a booking is "recently moved" when it has
  // been rescheduled OR updated within the last 24h. Used to help the team
  // notice last-minute moves at a glance.
  const isRecentlyModified = useCallback((resource) => {
    if (!resource) return false;
    const ts = resource.rescheduled_at || resource.updated_at;
    if (!ts) return false;
    try {
      const updated = new Date(ts).getTime();
      if (Number.isNaN(updated)) return false;
      return Date.now() - updated < 24 * 60 * 60 * 1000;
    } catch (e) {
      return false;
    }
  }, []);

  const recentlyModifiedTooltip = useCallback((resource) => {
    const ts = resource?.rescheduled_at || resource?.updated_at;
    if (!ts) return 'Récemment modifié';
    try {
      const d = new Date(ts);
      return `Modifié le ${format(d, 'dd/MM à HH:mm')}`;
    } catch (e) {
      return 'Récemment modifié';
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [availRes, bookingsRes, punctualRes, collabRes, profileRes, clientsRes, quoteRes, closuresRes, workHoursRes, leavesRes, slotTypesRes] = await Promise.all([
        companyApi.getAvailability(),
        companyApi.getBookings(),
        companyApi.getPunctualBookings(),
        companyApi.getCollaborators(),
        companyApi.getProfile(),
        companyApi.getClients(),
        companyApi.getQuoteRequests(), // Récupérer les demandes de devis
        companyApi.getClosures ? companyApi.getClosures() : Promise.resolve({ data: [] }), // Fermetures exceptionnelles
        companyApi.getWorkHours ? companyApi.getWorkHours() : Promise.resolve({ data: [] }), // Horaires de travail
        companyApi.getAllLeaves ? companyApi.getAllLeaves('approved') : Promise.resolve({ data: [] }), // Congés collaborateurs (APPROVED only)
        companyApi.getSlotTypes ? companyApi.getSlotTypes() : Promise.resolve({ data: [] }) // Types de créneaux
      ]);
      
      setAvailability(availRes.data || []);
      setBookings(bookingsRes.data || []);
      setPunctualBookings(punctualRes.data || []);
      setQuoteRequests(quoteRes.data || []);
      setCollaborators((collabRes.data || []).filter(c => c && c.is_active));
      setCompanyProfile(profileRes.data || {});
      setClients((clientsRes.data || []).filter(c => c && c.is_active));
      setClosures(closuresRes.data || []);
      setWorkHours(workHoursRes.data?.slots || []);
      setCollaboratorLeaves(leavesRes.data || []);
      setSlotTypes(slotTypesRes.data || []);

      // Helper: resolve a booking's start/end times & collab count based on its
      // slot_type (code), slot_period and the day's work hours periods.
      // Used for SUBSCRIPTION bookings (punctual bookings already have start/end).
      const _slotTypes = slotTypesRes.data || [];
      const _workHours = workHoursRes.data?.slots || [];
      const resolveBookingRange = (booking) => {
        const st = _slotTypes.find(s => s.code === booking.slot_type || s.id === booking.slot_type);
        const collaborators = st?.required_collaborators || 1;
        const code = (booking.slot_type || '').toString().toLowerCase();
        const name = (st?.name || '').toLowerCase();
        const period = booking.slot_period;

        // Find work hours for the booking date (0=Lundi … 6=Dimanche)
        const date = new Date(booking.date);
        const jsDay = date.getDay();
        const dow = jsDay === 0 ? 6 : jsDay - 1;
        const daySlot = _workHours.find(wh => wh.day_of_week === dow) || {};
        const morning = daySlot.morning || { start: '08:00', end: '12:00' };
        const afternoon = daySlot.afternoon || { start: '14:00', end: '18:00' };
        const fullDay = daySlot.full_day || { start: '08:00', end: '18:00' };

        const isFullDay =
          code === 'full_day' ||
          /journee|complete|full/.test(code) ||
          /journ[eé]e|complete|full/.test(name) ||
          (st?.duration_hours && st.duration_hours >= 6);
        const isMorning =
          period === 'morning' ||
          /^matin$|morning/.test(code) ||
          /^matin\b/.test(name);
        const isAfternoon =
          period === 'afternoon' ||
          /apres|aprem|afternoon/.test(code) ||
          /apr[eè]s|aprem|afternoon/.test(name);

        if (isFullDay) return { start: fullDay.start, end: fullDay.end, collaborators, slotType: st };
        if (isMorning) return { start: morning.start, end: morning.end, collaborators, slotType: st };
        if (isAfternoon) return { start: afternoon.start, end: afternoon.end, collaborators, slotType: st };
        // Generic half-day fallback
        return { start: morning.start, end: morning.end, collaborators, slotType: st };
      };
      
      // Helper: safely build a Date from a date string + time string. Returns null
      // if either is missing or yields an Invalid Date — prevents render crashes
      // when prod has half-formed bookings inherited from older versions.
      const safeDate = (dateStr, timeStr) => {
        try {
          if (!dateStr || !timeStr) return null;
          // Accept both plain YYYY-MM-DD and full ISO datetime strings
          // (some historical subscription bookings stored the client's
          // toISOString() as `date` — strip the time part defensively).
          const cleanDate = typeof dateStr === 'string' && dateStr.includes('T')
            ? dateStr.split('T')[0]
            : dateStr;
          const cleanTime = String(timeStr).length === 5 ? `${timeStr}:00` : timeStr;
          const d = new Date(`${cleanDate}T${cleanTime}`);
          return isNaN(d.getTime()) ? null : d;
        } catch (_e) {
          return null;
        }
      };

      // Convert to calendar events - ONLY show actual bookings, not availability
      const calendarEvents = [];
      
      // Add confirmed, pending_validation, AND in_progress bookings (subscription)
      bookingsRes.data.filter(b => ['confirmed', 'pending_validation', 'in_progress'].includes(b.status) && b.date).forEach(booking => {
        const client = clientsRes.data.find(c => c.id === booking.client_id);
        const range = resolveBookingRange(booking);
        const start = safeDate(booking.date, range.start);
        const end = safeDate(booking.date, range.end);
        if (!start || !end) return; // skip malformed entries
        const collaborator = collabRes.data.find(c => c.id === booking.collaborator_id);
        const isPending = booking.status === 'pending_validation';
        const isInProgress = booking.status === 'in_progress';
        const multiBadge = range.collaborators > 1 ? `👥×${range.collaborators} ` : '';
        const clientName = client?.name || booking.client_name || 'Client';

        calendarEvents.push({
          id: `booking-${booking.id}`,
          title: isPending
            ? `⏳ ${multiBadge}${clientName} (En attente)`
            : isInProgress
              ? `🔄 ${multiBadge}${clientName} (En cours)`
              : `${multiBadge}${clientName}`,
          start,
          end,
          type: 'booking',
          status: booking.status,
          isPendingValidation: isPending,
          requiresMultiCollab: range.collaborators > 1,
          requiredCollaborators: range.collaborators,
          collaborator_id: booking.collaborator_id,
          collaborator_name: collaborator?.name || 'Non assigné',
          resource: { ...booking, client_name: client?.name || booking.client_name }
        });
      });
      
      // Add punctual bookings (confirmed, pending, AND in_progress)
      punctualRes.data.filter(b => ['confirmed', 'pending', 'in_progress'].includes(b.status) && b.date && b.start_time).forEach(booking => {
        const collaborator = collabRes.data.find(c => c.id === booking.collaborator_id);
        const isInProgress = booking.status === 'in_progress';
        
        // Calculate end_time if not present (from duration)
        let endTime = booking.end_time;
        if (!endTime && booking.duration && booking.start_time) {
          try {
            const [startHour, startMin] = booking.start_time.split(':').map(Number);
            if (!isNaN(startHour) && !isNaN(startMin)) {
              const totalMinutes = startHour * 60 + startMin + (booking.duration || 60);
              endTime = `${Math.floor(totalMinutes / 60).toString().padStart(2, '0')}:${(totalMinutes % 60).toString().padStart(2, '0')}`;
            }
          } catch (_e) { /* keep default */ }
        }
        if (!endTime) endTime = booking.start_time; // Fallback
        const startD = safeDate(booking.date, booking.start_time);
        const endD = safeDate(booking.date, endTime);
        if (!startD || !endD) return;

        calendarEvents.push({
          id: `punctual-${booking.id}`,
          title: isInProgress 
            ? `🔄 ${booking.client_name || 'Client'} - ${booking.service_name || 'Service'}` 
            : `${booking.client_name || 'Client'} - ${booking.service_name || 'Service'}`,
          start: startD,
          end: endD,
          type: 'punctual',
          status: booking.status,
          collaborator_id: booking.collaborator_id,
          collaborator_name: collaborator?.name || 'Non assigné',
          resource: booking
        });
      });
      
      // Add quote requests (RDV commerciaux)
      (quoteRes.data || []).filter(q => 
        ['pending', 'rdv_confirmed', 'quote_to_do'].includes(q.status) && q.appointment_date
      ).forEach(quote => {
        const commercial = collabRes.data.find(c => c.id === quote.commercial_id);
        const startTime = quote.appointment_time || '10:00';
        let endTime;
        try {
          const endHour = parseInt(String(startTime).split(':')[0]) + 1;
          endTime = `${endHour.toString().padStart(2, '0')}:00`;
        } catch (_e) {
          endTime = '11:00';
        }
        const startD = safeDate(quote.appointment_date, startTime);
        const endD = safeDate(quote.appointment_date, endTime);
        if (!startD || !endD) return;

        calendarEvents.push({
          id: `quote-${quote.id}`,
          title: `RDV Devis - ${quote.client_name || 'Client'}`,
          start: startD,
          end: endD,
          type: 'quote',
          status: quote.status,
          collaborator_id: quote.commercial_id,
          collaborator_name: commercial?.name || 'Commercial',
          resource: quote
        });
      });
      
      setEvents(calendarEvents);
    } catch (error) {
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter events by collaborator
  const filteredEvents = selectedCollaborator === 'all' 
    ? events 
    : events.filter(e => e.collaborator_id === selectedCollaborator);

  const handleNavigate = (action) => {
    let newDate = new Date(currentDate);
    if (action === 'PREV') {
      if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
      else if (view === 'week') newDate = addDays(newDate, -7);
      else newDate = addDays(newDate, -1);
    } else if (action === 'NEXT') {
      if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
      else if (view === 'week') newDate = addDays(newDate, 7);
      else newDate = addDays(newDate, 1);
    } else {
      newDate = new Date();
    }
    setCurrentDate(newDate);
  };

  const handleCreateAvailability = async () => {
    if (!newAvail.date || newAvail.slots.length === 0) {
      toast.error('Date et créneaux sont obligatoires');
      return;
    }
    try {
      await companyApi.createAvailability(newAvail);
      toast.success('Disponibilité ajoutée');
      setIsDialogOpen(false);
      setNewAvail({ date: '', slots: [], collaborator_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteAvailability = async (id) => {
    try {
      await companyApi.deleteAvailability(id);
      toast.success('Disponibilité supprimée');
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCreateBookingForClient = async () => {
    if (!bookingForm.client_id || !bookingForm.date) {
      toast.error('Veuillez sélectionner un client et une date');
      return;
    }
    
    // Get collaborator IDs - support both single and multiple
    const collabIds = bookingForm.collaborator_ids?.length > 0 
      ? bookingForm.collaborator_ids 
      : [];
    
    // Detect if all selected collaborators are commercials → RDV mode
    const selectedCollabs = collaborators.filter(c => collabIds.includes(c.id));
    const isAppointmentMode = selectedCollabs.length > 0 && selectedCollabs.every(c => c.role === 'commercial');

    setIsSubmittingBooking(true);
    try {
      if (isAppointmentMode) {
        // RDV mode - one appointment per commercial collaborator
        const promises = collabIds.map(collabId =>
          companyApi.createAppointmentForClient({
            client_id: bookingForm.client_id,
            date: bookingForm.date,
            time_slot: bookingForm.time_slot,
            duration_minutes: parseInt(bookingForm.duration_minutes) || 60,
            collaborator_id: collabId,
            meeting_type: bookingForm.meeting_type || 'physical',
            reason: bookingForm.reason || 'RDV depuis le planning',
          })
        );
        await Promise.all(promises);
        toast.success(collabIds.length > 1 ? `${collabIds.length} RDV créés` : 'RDV créé avec succès');
      } else if (collabIds.length <= 1) {
        // Single collaborator or no collaborator - create one booking
        await companyApi.createBookingForClient({
          client_id: bookingForm.client_id,
          date: bookingForm.date,
          slot_type: bookingForm.slot_type,
          reason: bookingForm.reason || 'Réservation depuis le planning',
          collaborator_id: collabIds[0] || undefined
        });
        toast.success('Réservation créée avec succès');
      } else {
        // Multiple collaborators - create one booking per collaborator
        const promises = collabIds.map(collabId => 
          companyApi.createBookingForClient({
            client_id: bookingForm.client_id,
            date: bookingForm.date,
            slot_type: bookingForm.slot_type,
            reason: bookingForm.reason || 'Réservation depuis le planning',
            collaborator_id: collabId
          })
        );
        await Promise.all(promises);
        toast.success(`${collabIds.length} réservations créées avec succès`);
      }
      
      setIsBookingDialogOpen(false);
      setBookingForm({ client_id: '', date: '', slot_type: 'half_day', reason: '', collaborator_ids: [], time_slot: '09:00', duration_minutes: 60, meeting_type: 'physical' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  // Quick client creation
  const handleQuickClientCreate = async () => {
    if (!quickClientForm.name || !quickClientForm.email || !quickClientForm.password) {
      toast.error('Veuillez remplir le nom, l\'email et le mot de passe');
      return;
    }
    
    setIsCreatingClient(true);
    try {
      const response = await companyApi.createClient({
        name: quickClientForm.name,
        email: quickClientForm.email,
        phone: quickClientForm.phone || '',
        password: quickClientForm.password,
        create_subscription: false
      });
      
      // Add new client to list and select it
      const newClient = {
        id: response.data.client_id,
        name: quickClientForm.name,
        email: quickClientForm.email,
        phone: quickClientForm.phone || '',
        is_active: true
      };
      setClients(prev => [...prev, newClient]);
      setBookingForm(prev => ({ ...prev, client_id: response.data.client_id }));
      
      toast.success('Client créé avec succès');
      setIsQuickClientDialogOpen(false);
      setQuickClientForm({ name: '', email: '', phone: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du client');
    } finally {
      setIsCreatingClient(false);
    }
  };

  // Generate random password for quick client creation
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setQuickClientForm(prev => ({ ...prev, password }));
  };

  // Validate a pending punctual booking (optionally assign collaborator)
  const handleValidateBooking = async (bookingId, collaboratorId = null) => {
    setIsProcessing(true);
    try {
      // If collaborator is assigned, update the booking first
      if (collaboratorId) {
        await companyApi.updatePunctualBooking(bookingId, { collaborator_id: collaboratorId });
      }
      await companyApi.confirmPunctualBooking(bookingId);
      toast.success('Réservation validée avec succès');
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setIsProcessing(false);
    }
  };

  // Reject a pending punctual booking
  const handleRejectBooking = async (bookingId) => {
    setIsProcessing(true);
    try {
      await companyApi.updatePunctualBooking(bookingId, { status: 'cancelled' });
      toast.success('Réservation refusée');
      setSelectedEvent(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    } finally {
      setIsProcessing(false);
    }
  };

  // Cancel a confirmed booking
  const handleCancelBooking = async (bookingId, type) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) return;
    setIsProcessing(true);
    try {
      if (type === 'punctual') {
        await companyApi.updatePunctualBooking(bookingId, { status: 'cancelled' });
      } else {
        await companyApi.cancelBooking(bookingId);
      }
      toast.success('Réservation annulée');
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setIsProcessing(false);
    }
  };

  // Update booking (reschedule)
  const handleUpdateBooking = async () => {
    if (!selectedEvent) return;
    
    // Check if date changed and require a reason
    const originalDate = format(selectedEvent.start, 'yyyy-MM-dd');
    const dateChanged = editForm.date !== originalDate;
    
    if (dateChanged && !editForm.reschedule_reason) {
      toast.error('Veuillez indiquer un motif de modification de date');
      return;
    }
    
    if (dateChanged && editForm.reschedule_reason === 'other' && !editForm.reschedule_reason_custom) {
      toast.error('Veuillez préciser le motif de modification');
      return;
    }
    
    setIsProcessing(true);
    const bookingId = selectedEvent.resource?.id;

    // Build reschedule reason text
    let rescheduleReasonText = '';
    if (dateChanged) {
      if (editForm.reschedule_reason === 'other') {
        rescheduleReasonText = editForm.reschedule_reason_custom;
      } else {
        const reasonObj = RESCHEDULE_REASONS.find(r => r.value === editForm.reschedule_reason);
        rescheduleReasonText = reasonObj?.label || editForm.reschedule_reason;
      }
    }

    const buildPayload = (force = false) => ({
      date: editForm.date,
      collaborator_ids: editForm.collaborator_ids || [],
      slot_type: editForm.slot_type,
      slot_period: editForm.slot_period,
      client_id: editForm.client_id,
      notes: editForm.notes,
      reschedule_reason: rescheduleReasonText,
      original_date: dateChanged ? originalDate : null,
      force_move: force,
    });

    const performSave = async (force = false) => {
      const payload = buildPayload(force);
      if (selectedEvent.type === 'punctual') {
        await companyApi.updatePunctualBooking(bookingId, payload);
      } else if (selectedEvent.type === 'booking') {
        await companyApi.updateBooking(bookingId, payload);
      }
    };

    try {
      await performSave(false);
      toast.success('Réservation modifiée avec succès');
      setSelectedEvent(null);
      setIsEditMode(false);
      fetchData();
    } catch (error) {
      // Server refused because the target date/collab is unavailable — offer
      // the admin the conflict dialog with suggestions + a force override,
      // exactly like the drag & drop path already does. Without this, the
      // edit modal shows "[object Object]" and the change silently fails.
      const detail = error?.response?.data?.detail;
      if (
        error?.response?.status === 409
        && detail
        && typeof detail === 'object'
        && Array.isArray(detail.conflicts)
      ) {
        setMoveConflict({
          bookingId,
          type: selectedEvent.type,
          conflicts: detail.conflicts,
          payloadDate: editForm.date,
          collaboratorIds: editForm.collaborator_ids || [],
          perform: performSave,
          onApplySuggestion: async (suggestion, collabIdFromSuggestion) => {
            try {
              setIsProcessing(true);
              const payload = {
                ...buildPayload(false),
                date: suggestion.date,
                start_time: suggestion.start_time,
                end_time: suggestion.end_time,
                collaborator_ids: collabIdFromSuggestion ? [collabIdFromSuggestion] : (editForm.collaborator_ids || []),
              };
              if (selectedEvent.type === 'punctual') {
                await companyApi.updatePunctualBooking(bookingId, payload);
              } else {
                await companyApi.updateBooking(bookingId, payload);
              }
              toast.success('Réservation modifiée');
              setMoveConflict(null);
              setSelectedEvent(null);
              setIsEditMode(false);
              fetchData();
            } catch (e2) {
              toast.error('Erreur lors de la modification');
            } finally {
              setIsProcessing(false);
            }
          },
          onForceMove: async () => {
            try {
              setIsProcessing(true);
              await performSave(true);
              toast.success('Réservation forcée avec succès');
              setMoveConflict(null);
              setSelectedEvent(null);
              setIsEditMode(false);
              fetchData();
            } catch (e3) {
              toast.error(e3?.response?.data?.detail || 'Erreur lors du forçage');
            } finally {
              setIsProcessing(false);
            }
          },
        });
      } else {
        const msg = typeof detail === 'string' ? detail : 'Erreur lors de la modification';
        toast.error(msg);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', event.id);
    // Add visual feedback
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedEvent(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, day, hour, collabId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget({ day, hour, collabId });
  };

  const handleDragLeave = (e) => {
    setDropTarget(null);
  };

  const handleDrop = async (e, day, hour, collabId) => {
    e.preventDefault();
    setDropTarget(null);
    
    if (!draggedEvent) return;
    
    const newDate = format(day, 'yyyy-MM-dd');
    const bookingId = draggedEvent.resource?.id;
    
    // Calculate new start/end times based on drop hour
    const originalDuration = (draggedEvent.end - draggedEvent.start) / (1000 * 60 * 60);
    const newStartHour = hour;
    const newEndHour = newStartHour + originalDuration;
    const newStartTime = `${newStartHour.toString().padStart(2, '0')}:00`;
    const newEndTime = `${Math.floor(newEndHour).toString().padStart(2, '0')}:${(Math.round((newEndHour - Math.floor(newEndHour)) * 60)).toString().padStart(2, '0')}`;

    // Resolve the new collaborator set:
    //  • If the booking had multiple collabs and the user dropped onto a slot
    //    of an already-assigned collab → keep ALL of them (this is just a
    //    horizontal time move). Otherwise the previous behaviour (sending a
    //    single `collaborator_id`) was overwriting `collaborator_ids` and
    //    losing the other collabs from the booking.
    //  • If dropped onto a collab who wasn't assigned → swap the source collab
    //    for the target one, keeping the others.
    const existingIds = (draggedEvent.resource?.collaborator_ids && draggedEvent.resource.collaborator_ids.length)
      ? [...draggedEvent.resource.collaborator_ids]
      : (draggedEvent.collaborator_id ? [draggedEvent.collaborator_id] : []);
    let newCollabIds = existingIds;
    if (collabId) {
      if (existingIds.includes(collabId)) {
        // same swimlane (or another already-assigned one) → keep multi-collab
        newCollabIds = existingIds;
      } else if (draggedEvent.collaborator_id && existingIds.includes(draggedEvent.collaborator_id)) {
        // Replace just the source collab with the new target
        newCollabIds = existingIds.map(id => id === draggedEvent.collaborator_id ? collabId : id);
      } else {
        newCollabIds = [collabId];
      }
    }

    const performMove = async (force = false) => {
      const payload = {
        date: newDate,
        start_time: newStartTime,
        end_time: newEndTime,
        collaborator_ids: newCollabIds,
        force_move: force,
      };
      if (draggedEvent.type === 'punctual' || draggedEvent.type === 'quote') {
        await companyApi.updatePunctualBooking(bookingId, payload);
      } else {
        await companyApi.updateBooking(bookingId, payload);
      }
    };
    
    try {
      setIsProcessing(true);
      try {
        await performMove(false);
      } catch (err) {
        // Server detected the target collab(s) are not available — open the
        // conflict dialog with suggestions so the admin can pick one or
        // confirm the override.
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409 && detail && typeof detail === 'object' && Array.isArray(detail.conflicts)) {
          setMoveConflict({
            bookingId,
            type: draggedEvent.type,
            conflicts: detail.conflicts,
            payloadDate: newDate,
            payloadStart: newStartTime,
            payloadEnd: newEndTime,
            collaboratorIds: newCollabIds,
            perform: performMove,
            onApplySuggestion: async (suggestion, collabIdFromSuggestion) => {
              try {
                setIsProcessing(true);
                const payload = {
                  date: suggestion.date,
                  start_time: suggestion.start_time,
                  end_time: suggestion.end_time,
                  collaborator_ids: collabIdFromSuggestion ? [collabIdFromSuggestion] : newCollabIds,
                  force_move: false,
                };
                if (draggedEvent.type === 'punctual' || draggedEvent.type === 'quote') {
                  await companyApi.updatePunctualBooking(bookingId, payload);
                } else {
                  await companyApi.updateBooking(bookingId, payload);
                }
                toast.success(`Réservation déplacée au ${format(new Date(suggestion.date), 'EEEE d MMMM', { locale: fr })} à ${suggestion.start_time}`);
                setMoveConflict(null);
                fetchData();
              } catch (e2) {
                console.error(e2);
                toast.error('Erreur lors du déplacement');
              } finally {
                setIsProcessing(false);
              }
            },
          });
          return;
        }
        throw err;
      }
      
      toast.success(`Réservation déplacée au ${format(day, 'EEEE d MMMM', { locale: fr })} à ${newStartHour}h`);
      setDraggedEvent(null);
      fetchData();
    } catch (error) {
      console.error('Drop error:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors du déplacement');
    } finally {
      setIsProcessing(false);
    }
  };

  const openEditMode = () => {
    if (selectedEvent) {
      setEditForm({
        date: format(selectedEvent.start, 'yyyy-MM-dd'),
        slot_type: selectedEvent.resource?.slot_type || 'half_day',
        slot_period: selectedEvent.resource?.slot_period || '',
        collaborator_id: selectedEvent.collaborator_id || '',
        collaborator_ids: selectedEvent.resource?.collaborator_ids || 
                         (selectedEvent.collaborator_id ? [selectedEvent.collaborator_id] : []),
        client_id: selectedEvent.resource?.client_id || '',
        notes: selectedEvent.resource?.notes || '',
        reschedule_reason: '',
        reschedule_reason_custom: ''
      });
      setIsEditMode(true);
    }
  };

  // Handle photo selection for completion
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCompletePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle photo selection for start (before photo)
  const handleStartPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setStartPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setStartPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Start intervention with photo (before)
  const handleStartWithPhoto = async () => {
    if (!selectedEvent) return;
    
    setIsProcessing(true);
    try {
      const bookingId = selectedEvent.resource?.id;
      const bookingType = selectedEvent.type === 'punctual' ? 'punctual' : 'subscription';
      
      // Upload "before" photo if provided
      if (startPhoto) {
        await companyApi.uploadInterventionPhoto(bookingId, startPhoto, 'before', 'Photo avant intervention', true);
      }
      
      // Mark as in_progress
      if (bookingType === 'punctual') {
        await companyApi.updatePunctualBooking(bookingId, { status: 'in_progress' });
      } else {
        await companyApi.updateBookingStatus(bookingId, 'in_progress');
      }
      
      toast.success('Prestation démarrée ! Bonne intervention.');
      setShowStartDialog(false);
      setStartPhoto(null);
      setStartPhotoPreview(null);
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du démarrage');
    } finally {
      setIsProcessing(false);
    }
  };

  // Complete intervention with photo
  const handleCompleteWithPhoto = async () => {
    if (!selectedEvent) return;
    
    setIsProcessing(true);
    try {
      const bookingId = selectedEvent.resource?.id;
      const bookingType = selectedEvent.type === 'punctual' ? 'punctual' : 'subscription';
      
      // Upload "after" photo if provided
      if (completePhoto) {
        await companyApi.uploadInterventionPhoto(bookingId, completePhoto, 'after', 'Photo après intervention', true);
      }
      
      // Then mark as completed
      if (bookingType === 'punctual') {
        await companyApi.updatePunctualBooking(bookingId, { status: 'completed' });
      } else {
        await companyApi.updateBookingStatus(bookingId, 'completed');
      }
      
      toast.success('Prestation terminée avec succès !');
      setShowCompleteDialog(false);
      setCompletePhoto(null);
      setPhotoPreview(null);
      setSelectedEvent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la finalisation');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleSlot = (slot) => {
    if (newAvail.slots.includes(slot)) {
      setNewAvail({ ...newAvail, slots: newAvail.slots.filter(s => s !== slot) });
    } else {
      setNewAvail({ ...newAvail, slots: [...newAvail.slots, slot] });
    }
  };

  const eventStyleGetter = (event) => {
    let style = {
      borderRadius: '6px',
      opacity: 0.95,
      border: '0px',
      fontSize: '11px',
      padding: '2px 6px'
    };
    
    if (event.type === 'booking') {
      if (event.isPendingValidation) {
        // Orange avec bordure pointillée pour les réservations en attente
        style.backgroundColor = '#fef3c7'; // amber-100
        style.color = '#92400e'; // amber-800
        style.border = '2px dashed #f59e0b'; // amber-500
      } else if (event.status === 'in_progress') {
        // Cyan pour les prestations en cours
        style.backgroundColor = '#06b6d4'; // cyan-500
        style.color = '#fff';
        style.border = '2px solid #0891b2'; // cyan-600
      } else {
        style.backgroundColor = '#3b82f6'; // Blue for confirmed subscription bookings
        style.color = '#fff';
      }
      // Visual cue for multi-collaborator slot types (e.g. Journée 2 personnes)
      if (event.requiresMultiCollab) {
        style.boxShadow = 'inset 0 0 0 2px rgba(255,255,255,0.6)';
        style.fontWeight = '600';
      }
    } else if (event.type === 'punctual') {
      if (event.status === 'pending') {
        style.backgroundColor = '#f97316'; // Orange pending
        style.color = '#fff';
      } else if (event.status === 'in_progress') {
        // Cyan pour les prestations en cours
        style.backgroundColor = '#06b6d4'; // cyan-500
        style.color = '#fff';
        style.border = '2px solid #0891b2'; // cyan-600
      } else {
        style.backgroundColor = '#8b5cf6'; // Purple confirmed
        style.color = '#fff';
      }
    }
    
    return { style };
  };

  const handleSelectEvent = (event, e) => {
    // Alt+click provides a "quick duplicate" shortcut without opening the full modal
    if (e?.altKey) {
      setSelectedEvent(event);
      setShowDuplicateDialog(true);
      return;
    }
    setSelectedEvent(event);
  };

  const handleDuplicateBooking = async () => {
    if (!selectedEvent || !duplicateTargetDate) {
      toast.error('Choisissez une date de destination');
      return;
    }
    setIsProcessing(true);
    try {
      const bookingId = selectedEvent.resource?.id;
      const bookingType = selectedEvent.type === 'punctual' ? 'punctual' : 'subscription';
      await companyApi.duplicateBooking(bookingId, {
        new_date: duplicateTargetDate,
        booking_type: bookingType,
      });
      toast.success(`Réservation dupliquée au ${format(new Date(duplicateTargetDate), 'd MMMM yyyy', { locale: fr })}`);
      setShowDuplicateDialog(false);
      setDuplicateTargetDate('');
      setSelectedEvent(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Duplication impossible');
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── react-big-calendar drag & drop ───────────────────────────────────
  // Convert a JS Date into HH:mm string.
  const _hhmm = (d) => format(d, 'HH:mm');

  // Persist a booking's new date / time range. Handles both punctual and
  // subscription types, plus the 409 conflict flow (opens the existing
  // MoveConflict dialog so the admin can pick a suggestion or force).
  const persistCalendarMove = useCallback(async (event, start, end, { duplicate = false } = {}) => {
    if (!event || !event.resource?.id) return;
    // We currently only support drag/resize on real bookings — quotes
    // ("RDV commerciaux") are read-only in the planning to avoid changing
    // an appointment scheduled through the CRM side by accident.
    if (event.type === 'quote') {
      toast.info('Les RDV commerciaux se déplacent depuis leur fiche.');
      return;
    }
    const bookingId = event.resource.id;
    const newDate = format(start, 'yyyy-MM-dd');
    const newStart = _hhmm(start);
    const newEnd = _hhmm(end);

    // Duplicate branch (Alt + drag) → clone at target date, no move.
    if (duplicate) {
      try {
        setIsProcessing(true);
        await companyApi.duplicateBooking(bookingId, {
          new_date: newDate,
          booking_type: event.type === 'punctual' ? 'punctual' : 'subscription',
        });
        toast.success(`Réservation dupliquée au ${format(start, 'EEEE d MMMM', { locale: fr })}`);
        fetchData();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Duplication impossible');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    const collabIds = (event.resource?.collaborator_ids && event.resource.collaborator_ids.length)
      ? event.resource.collaborator_ids
      : (event.collaborator_id ? [event.collaborator_id] : []);

    const performMove = async (force = false) => {
      const payload = {
        date: newDate,
        start_time: newStart,
        end_time: newEnd,
        collaborator_ids: collabIds,
        force_move: force,
      };
      if (event.type === 'punctual') {
        await companyApi.updatePunctualBooking(bookingId, payload);
      } else {
        await companyApi.updateBooking(bookingId, payload);
      }
    };

    // Optimistic UI: update local state instantly, rollback on error.
    const prevEvents = events;
    setEvents((cur) => cur.map((e) => (e.id === event.id ? { ...e, start, end } : e)));

    try {
      setIsProcessing(true);
      await performMove(false);
      toast.success(`Réservation déplacée au ${format(start, 'EEEE d MMMM', { locale: fr })} à ${newStart}`);
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && typeof detail === 'object' && Array.isArray(detail.conflicts)) {
        // Rollback optimistic move — we'll re-apply once the user confirms.
        setEvents(prevEvents);
        setMoveConflict({
          bookingId,
          type: event.type,
          conflicts: detail.conflicts,
          payloadDate: newDate,
          payloadStart: newStart,
          payloadEnd: newEnd,
          collaboratorIds: collabIds,
          perform: performMove,
          onApplySuggestion: async (suggestion, collabIdFromSuggestion) => {
            try {
              setIsProcessing(true);
              const payload = {
                date: suggestion.date,
                start_time: suggestion.start_time,
                end_time: suggestion.end_time,
                collaborator_ids: collabIdFromSuggestion ? [collabIdFromSuggestion] : collabIds,
                force_move: false,
              };
              if (event.type === 'punctual') {
                await companyApi.updatePunctualBooking(bookingId, payload);
              } else {
                await companyApi.updateBooking(bookingId, payload);
              }
              toast.success(`Réservation déplacée au ${format(new Date(suggestion.date), 'EEEE d MMMM', { locale: fr })} à ${suggestion.start_time}`);
              setMoveConflict(null);
              fetchData();
            } catch (e2) {
              toast.error('Erreur lors du déplacement');
            } finally {
              setIsProcessing(false);
            }
          },
        });
        return;
      }
      // Any other error → rollback and surface message.
      setEvents(prevEvents);
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors du déplacement');
    } finally {
      setIsProcessing(false);
    }
  }, [events, fetchData]);

  // onEventDrop fires when the user releases an event on a new time/day.
  // The `isAllDay` flag is provided by react-big-calendar; we ignore it
  // since our planning uses timed slots.
  const handleCalendarEventDrop = useCallback(({ event, start, end }) => {
    // Detect the current keyboard modifier — react-big-calendar doesn't
    // pass the DOM event to onEventDrop, so we snapshot Alt via a ref
    // that a document-level listener keeps up to date.
    const altPressed = altKeyRef.current;
    return persistCalendarMove(event, start, end, { duplicate: altPressed });
  }, [persistCalendarMove]);

  const handleCalendarEventResize = useCallback(({ event, start, end }) => {
    return persistCalendarMove(event, start, end, { duplicate: false });
  }, [persistCalendarMove]);

  // Only real bookings (subscription + punctual) can be dragged/resized.
  // Quote/RDV commercial events are not draggable to avoid accidental CRM
  // rescheduling from the planning grid.
  const isEventDraggable = useCallback((event) => (
    event?.type === 'booking' || event?.type === 'punctual'
  ), []);

  // Get events for a specific day (for day view print)
  const getDayEvents = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredEvents.filter(e => format(e.start, 'yyyy-MM-dd') === dateStr)
      .sort((a, b) => a.start - b.start);
  };
  
  // Get team schedule for a specific day
  const getTeamSchedule = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return collaborators.map(collab => {
      const collabEvents = events.filter(
        e => e.collaborator_id === collab.id && format(e.start, 'yyyy-MM-dd') === dateStr
      );
      return {
        ...collab,
        events: collabEvents,
        isAvailable: collabEvents.length === 0
      };
    });
  };

  // Get week days starting from Monday
  const getWeekDays = (date) => {
    const start = startOfWeek(date, { weekStartsOn: 1 });
    // Lundi à Samedi (6 jours, pas de Dimanche)
    return Array.from({ length: 6 }, (_, i) => addDays(start, i));
  };

  // Heures de la journée (8h à 19h)
  const WORK_HOURS = Array.from({ length: 12 }, (_, i) => i + 8); // 8, 9, 10, ..., 19

  // Get team weekly schedule
  const getTeamWeeklySchedule = () => {
    const weekDays = getWeekDays(currentDate);
    return collaborators.map(collab => {
      const weekSchedule = weekDays.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const dayEvents = events.filter(
          e => e.collaborator_id === collab.id && format(e.start, 'yyyy-MM-dd') === dateStr
        );
        return {
          date: day,
          dateStr,
          events: dayEvents,
          totalHours: dayEvents.reduce((sum, e) => {
            const duration = (e.end - e.start) / (1000 * 60 * 60);
            return sum + duration;
          }, 0)
        };
      });
      
      const totalWeekHours = weekSchedule.reduce((sum, day) => sum + day.totalHours, 0);
      const totalWeekEvents = weekSchedule.reduce((sum, day) => sum + day.events.length, 0);
      
      return {
        ...collab,
        weekSchedule,
        totalWeekHours,
        totalWeekEvents
      };
    });
  };

  // Get events for a specific collaborator, date and hour
  const getEventsForHour = (collabId, date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(e => {
      // Si collabId est null, on cherche les événements sans collaborateur assigné
      if (collabId === null) {
        if (e.collaborator_id) return false; // A un collaborateur = pas dans "Non assigné"
      } else {
        if (e.collaborator_id !== collabId) return false;
      }
      if (format(e.start, 'yyyy-MM-dd') !== dateStr) return false;
      const eventStartHour = e.start.getHours();
      const eventEndHour = e.end.getHours();
      return hour >= eventStartHour && hour < eventEndHour;
    });
  };

  // Check if this is the start hour of an event
  const isEventStart = (event, hour) => {
    return event.start.getHours() === hour;
  };

  // Get event duration in hours
  const getEventDuration = (event) => {
    return (event.end - event.start) / (1000 * 60 * 60);
  };

  // Calculate occupancy rate for a collaborator for the week
  const getCollaboratorOccupancy = useCallback((collabId) => {
    const weekDays = getWeekDays(currentDate);
    const workingDays = weekDays.filter(day => !getHolidayForDate(day) && !getClosureForDate(day));
    
    // Assuming 8 working hours per day (8h-12h + 14h-18h)
    const maxHoursPerWeek = workingDays.length * 8;
    
    let bookedHours = 0;
    weekDays.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayEvents = events.filter(e => 
        e.collaborator_id === collabId && 
        format(e.start, 'yyyy-MM-dd') === dateStr
      );
      dayEvents.forEach(event => {
        bookedHours += getEventDuration(event);
      });
    });
    
    const occupancyRate = maxHoursPerWeek > 0 ? (bookedHours / maxHoursPerWeek) * 100 : 0;
    
    return {
      bookedHours: Math.round(bookedHours * 10) / 10,
      maxHours: maxHoursPerWeek,
      rate: Math.round(occupancyRate),
      color: occupancyRate < 30 ? 'green' : occupancyRate < 70 ? 'yellow' : 'red',
      label: occupancyRate < 30 ? 'Disponible' : occupancyRate < 70 ? 'Modéré' : 'Chargé'
    };
  }, [currentDate, events, getHolidayForDate, getClosureForDate]);

  // Get events for a collaborator on a specific day
  const getCollaboratorDayEvents = useCallback((collabId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(e => 
      e.collaborator_id === collabId && 
      format(e.start, 'yyyy-MM-dd') === dateStr
    ).sort((a, b) => a.start - b.start);
  }, [events]);

  // Toggle member collapse
  const toggleMemberCollapse = (memberId) => {
    setCollapsedMembers(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }));
  };

  // Collapse/Expand all members
  const collapseAllMembers = () => {
    const allCollapsed = {};
    collaborators.forEach(c => { allCollapsed[c.id] = true; });
    setCollapsedMembers(allCollapsed);
  };

  const expandAllMembers = () => {
    setCollapsedMembers({});
  };

  // Calculate total team occupancy for the week
  const getTeamOccupancy = useCallback(() => {
    if (collaborators.length === 0) return { rate: 0, bookedHours: 0, maxHours: 0 };
    
    let totalBookedHours = 0;
    let totalMaxHours = 0;
    
    collaborators.forEach(collab => {
      const occupancy = getCollaboratorOccupancy(collab.id);
      totalBookedHours += occupancy.bookedHours;
      totalMaxHours += occupancy.maxHours;
    });
    
    const rate = totalMaxHours > 0 ? (totalBookedHours / totalMaxHours) * 100 : 0;
    
    return {
      bookedHours: Math.round(totalBookedHours * 10) / 10,
      maxHours: totalMaxHours,
      rate: Math.round(rate),
      color: rate < 30 ? 'green' : rate < 70 ? 'yellow' : 'red',
      label: rate < 30 ? 'Disponible' : rate < 70 ? 'Modéré' : 'Chargé'
    };
  }, [collaborators, getCollaboratorOccupancy]);

  // Check if a day has working hours configured
  const isWorkingDay = useCallback((date) => {
    if (!Array.isArray(workHours) || workHours.length === 0) return true; // Default to open if no config
    
    const dayOfWeek = getDay(date);
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert JS Sunday=0 to Mon=0, Sun=6
    
    // Check if there are active work hours for this day
    const dayWorkHours = workHours.filter(wh => wh.day_of_week === adjustedDay && wh.is_active);
    return dayWorkHours.length > 0;
  }, [workHours]);

  // Calculate team occupancy for a specific day
  const getTeamDayOccupancy = useCallback((date) => {
    if (collaborators.length === 0) return { rate: 0, bookedHours: 0, freeSlots: 0 };
    
    // Check if day is closed (holiday, closure, or no working hours)
    const isClosed = getHolidayForDate(date) || getClosureForDate(date) || !isWorkingDay(date);
    if (isClosed) return { rate: 0, bookedHours: 0, freeSlots: 0, closed: true };
    
    const maxHoursPerDay = 8; // 8h par collaborateur par jour
    let totalBookedHours = 0;
    let freeCollaborators = 0;
    
    collaborators.forEach(collab => {
      const dayEvents = getCollaboratorDayEvents(collab.id, date);
      let collabBookedHours = 0;
      dayEvents.forEach(event => {
        collabBookedHours += getEventDuration(event);
      });
      totalBookedHours += collabBookedHours;
      
      // Consider collaborator "free" if less than 4h booked
      if (collabBookedHours < 4) {
        freeCollaborators++;
      }
    });
    
    const totalMaxHours = collaborators.length * maxHoursPerDay;
    const rate = totalMaxHours > 0 ? (totalBookedHours / totalMaxHours) * 100 : 0;
    
    return {
      bookedHours: Math.round(totalBookedHours * 10) / 10,
      maxHours: totalMaxHours,
      rate: Math.round(rate),
      freeCollaborators,
      totalCollaborators: collaborators.length,
      color: rate < 30 ? 'green' : rate < 70 ? 'yellow' : 'red'
    };
  }, [collaborators, getCollaboratorDayEvents, getHolidayForDate, getClosureForDate, isWorkingDay]);

  // Get lunch break time from work hours (typically 12:00-14:00)
  const getLunchBreak = useCallback((date) => {
    // Ensure workHours is an array
    if (!Array.isArray(workHours) || workHours.length === 0) return null;
    
    const dayOfWeek = getDay(date);
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Mon=0
    
    // Find work hours for this day
    const dayWorkHours = workHours.filter(wh => wh.day_of_week === adjustedDay && wh.is_active);
    
    if (dayWorkHours.length === 0) return null;
    
    // If there are 2 slots (morning and afternoon), the gap is lunch break
    if (dayWorkHours.length >= 2) {
      const sortedSlots = [...dayWorkHours].sort((a, b) => 
        a.start_time.localeCompare(b.start_time)
      );
      
      // Gap between first slot end and second slot start is lunch
      const morningEnd = sortedSlots[0].end_time;
      const afternoonStart = sortedSlots[1].start_time;
      
      if (morningEnd < afternoonStart) {
        return {
          start: morningEnd,
          end: afternoonStart,
          label: 'Pause déjeuner'
        };
      }
    }
    
    return null;
  }, [workHours]);

  // Export PDF - Support for day view and week/team view
  const exportPDFPerMember = () => {
    try {
      // Convert AdvancedPlanning's events to the format expected by exportPlanningPDF
      const collabById = {};
      collaborators.forEach(c => { collabById[c.id] = c; });

      const wrappedEvents = (events || []).map(e => {
        const oid = e.resource?.collaborator_id || null;
        const owner = oid ? collabById[oid] : null;
        const isAppt = e.resource?.booking_type === 'appointment';
        const isPunctual = e.resource?.booking_type === 'punctual';
        let typeKey = 'collab_subscription';
        if (isAppt) {
          typeKey = (e.resource?.meeting_type === 'online' || e.resource?.meeting_type === 'video')
            ? 'commercial_video' : 'commercial_physical';
        } else if (isPunctual) {
          typeKey = 'collab_punctual';
        }
        return {
          start: e.start,
          end: e.end,
          title: e.title || e.resource?.client_name || 'Réservation',
          resource: {
            ...e.resource,
            _kind: isAppt ? 'appointment' : 'intervention',
            _type: typeKey,
            _ownerId: oid,
            _ownerName: owner?.name || 'Non assigné',
            _ownerRole: owner?.role || null,
          },
        };
      });

      // Build period label from current view
      const startWk = startOfWeek(currentDate, { weekStartsOn: 1 });
      const endWk = addDays(startWk, 6);
      let periodLabel;
      if (view === 'month') {
        periodLabel = format(currentDate, 'MMMM yyyy', { locale: fr });
      } else if (view === 'day') {
        periodLabel = format(currentDate, 'EEEE d MMMM yyyy', { locale: fr });
      } else {
        periodLabel = `${format(startWk, 'd MMM', { locale: fr })} → ${format(endWk, 'd MMM yyyy', { locale: fr })}`;
      }

      const eventTypes = {
        commercial_physical: { label: 'RDV physique (Commercial)', bg: '#7c3aed' },
        commercial_video: { label: 'RDV visio (Commercial)', bg: '#6366f1' },
        collab_punctual: { label: 'Intervention ponctuelle', bg: '#10b981' },
        collab_subscription: { label: 'Intervention abonnement', bg: '#0d9488' },
      };

      exportPlanningPDF({
        events: wrappedEvents,
        collaborators,
        companyName: companyProfile?.name || '',
        periodLabel,
        eventTypes,
        resolveStyle: null,
        mode: 'per-member',
      });
      toast.success('PDF par membre généré');
    } catch (err) {
      console.error('PDF per-member error', err);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF('landscape'); // Landscape for team view
    
    // Header with logo
    const logoX = 15;
    let contentStartY = 50;
    
    if (companyProfile?.logo_url) {
      try {
        doc.addImage(companyProfile.logo_url, 'PNG', logoX, 10, 25, 25);
      } catch (e) {
        console.log('Could not add logo');
      }
    }
    
    // Company name
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(companyProfile?.name || 'Planning', companyProfile?.logo_url ? 45 : 15, 20);
    
    // View type and date range
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    
    if (view === 'month') {
      // Monthly view - show week-by-week summary
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const dateStr = format(currentDate, 'MMMM yyyy', { locale: fr });
      doc.text(`Planning du mois de ${dateStr}`, companyProfile?.logo_url ? 45 : 15, 30);
      
      // Get all events for the month
      const monthEvents = events.filter(e => 
        e.start >= monthStart && e.start <= monthEnd
      );
      
      // Group by collaborator
      const eventsByCollab = {};
      collaborators.forEach(c => {
        eventsByCollab[c.id] = { name: c.name, events: [] };
      });
      eventsByCollab['none'] = { name: 'Non assigné', events: [] };
      
      monthEvents.forEach(event => {
        const collabId = event.resource?.collaborator_id || 'none';
        if (eventsByCollab[collabId]) {
          eventsByCollab[collabId].events.push(event);
        }
      });
      
      // Create table with collaborators summary
      const tableData = Object.values(eventsByCollab)
        .filter(c => c.events.length > 0)
        .map(c => [
          c.name,
          c.events.length.toString(),
          c.events.filter(e => e.status === 'confirmed').length.toString(),
          c.events.filter(e => e.status === 'completed').length.toString()
        ]);
      
      if (tableData.length > 0) {
        doc.autoTable({
          startY: 45,
          head: [['Collaborateur', 'Total RDV', 'Confirmés', 'Terminés']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [47, 82, 51], textColor: 255 },
          styles: { fontSize: 10 }
        });
      }
      
    } else if (view === 'week') {
      // Weekly team view - show each collaborator's schedule
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekDays = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) });
      const dateStr = `Semaine du ${format(weekStart, 'd MMMM', { locale: fr })} au ${format(addDays(weekStart, 6), 'd MMMM yyyy', { locale: fr })}`;
      doc.text(`Planning équipe - ${dateStr}`, companyProfile?.logo_url ? 45 : 15, 30);
      
      // Create headers: Collaborator + each day
      const headers = ['Collaborateur', ...weekDays.map(d => format(d, 'EEE dd', { locale: fr }))];
      
      // Build table data per collaborator
      const tableData = collaborators.map(collab => {
        const row = [collab.name];
        weekDays.forEach(day => {
          const dayStart = new Date(day.setHours(0, 0, 0, 0));
          const dayEnd = new Date(day.setHours(23, 59, 59, 999));
          const dayEvents = events.filter(e => 
            e.start >= dayStart && e.start <= dayEnd &&
            e.resource?.collaborator_id === collab.id
          );
          if (dayEvents.length > 0) {
            row.push(dayEvents.map(e => 
              format(e.start, 'HH:mm') + (e.resource?.client_name ? `\n${e.resource.client_name.substring(0, 10)}` : '')
            ).join('\n---\n'));
          } else {
            row.push('-');
          }
        });
        return row;
      });
      
      if (tableData.length > 0) {
        doc.autoTable({
          startY: 45,
          head: [headers],
          body: tableData,
          theme: 'grid',
          headStyles: { fillColor: [47, 82, 51], textColor: 255, fontSize: 8 },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: { 0: { fontStyle: 'bold', cellWidth: 35 } }
        });
      }
      
    } else {
      // Day view - detailed schedule
      const dayEvents = getDayEvents(currentDate);
      const dateStr = format(currentDate, 'EEEE d MMMM yyyy', { locale: fr });
      doc.text(`Programme du ${dateStr}`, companyProfile?.logo_url ? 45 : 15, 30);
      
      // Group by collaborator
      const eventsByCollab = {};
      collaborators.forEach(c => {
        eventsByCollab[c.id] = { name: c.name, events: [] };
      });
      eventsByCollab['none'] = { name: 'Non assigné', events: [] };
      
      dayEvents.forEach(event => {
        const collabId = event.resource?.collaborator_id || 'none';
        if (eventsByCollab[collabId]) {
          eventsByCollab[collabId].events.push(event);
        } else {
          eventsByCollab['none'].events.push(event);
        }
      });
      
      let yPosition = 50;
      
      Object.values(eventsByCollab).filter(c => c.events.length > 0).forEach(collab => {
        // Collaborator name
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(47, 82, 51);
        doc.text(collab.name, 15, yPosition);
        yPosition += 5;
        
        const tableData = collab.events.map(event => [
          format(event.start, 'HH:mm') + ' - ' + format(event.end, 'HH:mm'),
          event.type === 'punctual' ? event.resource?.service_name : (event.status === 'available' ? 'Disponible' : 'Réservé'),
          event.type === 'punctual' ? event.resource?.client_name : '-',
          event.status === 'pending' ? 'En attente' : (event.status === 'confirmed' ? 'Confirmé' : event.status === 'completed' ? 'Terminé' : 'Disponible')
        ]);
        
        doc.autoTable({
          startY: yPosition,
          head: [['Horaire', 'Service', 'Client', 'Statut']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [100, 100, 100], textColor: 255, fontSize: 9 },
          styles: { fontSize: 9 },
          margin: { left: 15 }
        });
        
        yPosition = doc.lastAutoTable.finalY + 10;
      });
      
      if (dayEvents.length === 0) {
        doc.setFontSize(12);
        doc.setTextColor(128, 128, 128);
        doc.text('Aucun événement prévu pour cette journée.', 15, 55);
      }
    }
    
    // Line separator
    doc.setDrawColor(200, 200, 200);
    doc.line(15, 40, doc.internal.pageSize.width - 15, 40);
    
    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')} - ResaWork`, 15, pageHeight - 10);
    
    // Save
    const viewName = view === 'month' ? 'mois' : view === 'week' ? 'semaine' : 'jour';
    doc.save(`planning-equipe-${viewName}-${format(currentDate, 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF généré avec succès');
  };

  // Print day view
  const printDayView = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PlanningGuideTour />
      <PlanningHowItWorks />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold">Planning</h1>
          <p className="text-muted-foreground mt-1">Gérez vos réservations et voyez les disponibilités de l'équipe</p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* View Mode Toggle */}
          <div className="flex border rounded-lg overflow-hidden mr-2">
            <Button
              variant={viewMode === 'members' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('members')}
              className="rounded-none"
            >
              <Grid3X3 className="w-4 h-4 mr-1" />
              Membres
            </Button>
            <Button
              variant={viewMode === 'team' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('team')}
              className="rounded-none"
            >
              <List className="w-4 h-4 mr-1" />
              Journées
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className="rounded-none"
            >
              <CalendarIcon className="w-4 h-4 mr-1" />
              Calendrier
            </Button>
          </div>

          {/* Collaborator Filter */}
          {viewMode === 'calendar' && (
            <Select value={selectedCollaborator} onValueChange={setSelectedCollaborator}>
              <SelectTrigger className="w-40">
                <User className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Collaborateur" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {collaborators.map(collab => (
                  <SelectItem key={collab.id} value={collab.id}>{collab.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* View buttons */}
          {viewMode === 'calendar' && (
            <div className="flex border rounded-lg overflow-hidden">
              <Button
                variant={view === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('month')}
                className="rounded-none"
              >
                <Grid3X3 className="w-4 h-4 mr-1" />
                Mois
              </Button>
              <Button
                variant={view === 'week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('week')}
                className="rounded-none border-x"
              >
                <CalendarIcon className="w-4 h-4 mr-1" />
                Semaine
              </Button>
              <Button
                variant={view === 'day' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('day')}
                className="rounded-none border-r"
              >
                <List className="w-4 h-4 mr-1" />
                Jour
              </Button>
              <Button
                variant={view === 'agenda' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setView('agenda')}
                className="rounded-none"
              >
                <FileText className="w-4 h-4 mr-1" />
                Agenda
              </Button>
            </div>
          )}

          {/* Export buttons */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-pdf-btn">
                <Download className="w-4 h-4 mr-1" />
                PDF
                <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Format d'export</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={exportPDF} data-testid="export-pdf-global">
                <Download className="w-4 h-4 mr-2" />
                Planning global
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDFPerMember()} data-testid="export-pdf-per-member">
                <Users className="w-4 h-4 mr-2" />
                Une page par membre
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={printDayView}>
            <Printer className="w-4 h-4 mr-1" />
            Imprimer
          </Button>

          {/* Add new booking - dialog moved from availability to booking */}

          {/* Book for client */}
          <Dialog open={isBookingDialogOpen} onOpenChange={setIsBookingDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="book-for-client-btn">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Réserver</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md w-[calc(100%-1rem)] max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="font-heading">Réserver pour un client</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <div className="flex gap-2">
                    <Select
                      value={bookingForm.client_id}
                      onValueChange={(value) => setBookingForm({ ...bookingForm, client_id: value })}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Sélectionner un client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name} - {c.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setIsQuickClientDialogOpen(true)}
                      title="Créer un nouveau client"
                      data-testid="quick-add-client-btn"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value })}
                  />
                </div>
                {(() => {
                  // Detect if all selected collaborators are commercials → show RDV mode
                  const selectedCollabsForMode = collaborators.filter(c => (bookingForm.collaborator_ids || []).includes(c.id));
                  const isApptMode = selectedCollabsForMode.length > 0 && selectedCollabsForMode.every(c => c.role === 'commercial');
                  if (isApptMode) {
                    return (
                      <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-4" data-testid="rdv-commercial-section">
                        <div className="flex items-center gap-2 text-purple-700 text-sm font-medium">
                          <Briefcase className="w-4 h-4" />
                          Mode RDV commercial — créneau spécifique (pas demi-journée)
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label>Heure du RDV *</Label>
                            <Input
                              type="time"
                              data-testid="rdv-time-input"
                              value={bookingForm.time_slot}
                              onChange={(e) => setBookingForm({ ...bookingForm, time_slot: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Durée *</Label>
                            <Select
                              value={String(bookingForm.duration_minutes)}
                              onValueChange={(v) => setBookingForm({ ...bookingForm, duration_minutes: parseInt(v) })}
                            >
                              <SelectTrigger data-testid="rdv-duration-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="15">15 minutes</SelectItem>
                                <SelectItem value="30">30 minutes</SelectItem>
                                <SelectItem value="45">45 minutes</SelectItem>
                                <SelectItem value="60">1 heure</SelectItem>
                                <SelectItem value="90">1h30</SelectItem>
                                <SelectItem value="120">2 heures</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Type de RDV</Label>
                          <Select
                            value={bookingForm.meeting_type}
                            onValueChange={(v) => setBookingForm({ ...bookingForm, meeting_type: v })}
                          >
                            <SelectTrigger data-testid="rdv-meeting-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="physical">En présentiel</SelectItem>
                              <SelectItem value="online">Visioconférence</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          ℹ️ Le créneau doit respecter les horaires de travail du commercial. En cas de visio, vous pourrez ajouter le lien Google Meet ensuite.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      <Label>Type de créneau</Label>
                      <Select
                        value={bookingForm.slot_type}
                        onValueChange={(value) => {
                          // Reset collaborators if changing slot type
                          setBookingForm({ ...bookingForm, slot_type: value, collaborator_ids: [] });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {slotTypes.filter(st => st.is_active).map((st) => (
                            <SelectItem key={st.id || st.code} value={st.code}>
                              <div className="flex flex-col">
                                <span>
                                  {st.name} ({st.duration_hours}h)
                                  {st.required_collaborators > 1 ? ` - ${st.required_collaborators} pers.` : ''}
                                  {st.pricing_mode === 'fixed' && st.fixed_price ? ` - ${st.fixed_price}€` : ''}
                                </span>
                            {st.time_slots && st.time_slots.length > 0 && (
                              <span className="text-xs text-muted-foreground">
                                {st.time_slots.map(ts => `${ts.name}: ${ts.start_time}-${ts.end_time}`).join(' | ')}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                      {slotTypes.length === 0 && (
                        <>
                          <SelectItem value="half_day">Demi-journée (4h)</SelectItem>
                          <SelectItem value="full_day">Journée complète (7h)</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const selectedSlotType = slotTypes.find(st => st.code === bookingForm.slot_type);
                    if (selectedSlotType) {
                      return (
                        <div className="space-y-1">
                          {selectedSlotType.required_collaborators > 1 && (
                            <p className="text-xs text-amber-600 font-medium">
                              ⚠️ Ce type de créneau nécessite {selectedSlotType.required_collaborators} collaborateurs
                            </p>
                          )}
                          {selectedSlotType.time_slots && selectedSlotType.time_slots.length > 0 && (
                            <p className="text-xs text-blue-600">
                              📅 Horaires : {selectedSlotType.time_slots.map(ts => `${ts.name} (${ts.start_time}-${ts.end_time})`).join(', ')}
                            </p>
                          )}
                          {selectedSlotType.pricing_mode === 'fixed' && selectedSlotType.fixed_price && (
                            <p className="text-xs text-green-600 font-medium">
                              💰 Prix fixe : {selectedSlotType.fixed_price}€
                            </p>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                  );
                })()}
                <div className="space-y-2">
                  <Label>Collaborateurs assignés</Label>
                  {(() => {
                    const selectedSlotType = slotTypes.find(st => st.code === bookingForm.slot_type);
                    const requiredCount = selectedSlotType?.required_collaborators || 1;
                    const selectedCount = bookingForm.collaborator_ids?.length || 0;
                    
                    return (
                      <p className={`text-xs mb-2 ${
                        requiredCount > 1 && selectedCount !== requiredCount 
                          ? 'text-amber-600 font-medium' 
                          : 'text-muted-foreground'
                      }`}>
                        {requiredCount > 1 
                          ? `Sélectionnez exactement ${requiredCount} collaborateurs (${selectedCount}/${requiredCount} sélectionnés)`
                          : 'Cochez plusieurs collaborateurs pour créer une intervention commune'
                        }
                      </p>
                    );
                  })()}
                  <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {collaborators.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">Aucun collaborateur</p>
                    ) : (
                      collaborators.map((collab) => {
                        const isChecked = bookingForm.collaborator_ids?.includes(collab.id);
                        return (
                          <div 
                            key={collab.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              isChecked ? 'bg-primary/10 border border-primary' : 'hover:bg-muted/50'
                            }`}
                            onClick={() => {
                              const currentIds = bookingForm.collaborator_ids || [];
                              const newIds = isChecked 
                                ? currentIds.filter(id => id !== collab.id)
                                : [...currentIds, collab.id];
                              setBookingForm({ ...bookingForm, collaborator_ids: newIds });
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}}
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            {collab.avatar_url ? (
                              <img 
                                src={getAssetUrl(collab.avatar_url)}
                                alt={collab.name}
                                className="w-8 h-8 rounded-full object-cover" loading="lazy" decoding="async"
                              />
                            ) : (
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                                style={{ backgroundColor: collab.color || '#6366f1' }}
                              >
                                {collab.name?.charAt(0)?.toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm">{collab.name}</p>
                              {collab.role === 'commercial' && (
                                <span className="text-[10px] text-amber-600">Commercial</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {bookingForm.collaborator_ids?.length > 0 && (
                    <p className="text-xs text-primary font-medium">
                      {bookingForm.collaborator_ids.length} collaborateur{bookingForm.collaborator_ids.length > 1 ? 's' : ''} sélectionné{bookingForm.collaborator_ids.length > 1 ? 's' : ''}
                    </p>
                  )}
                  {bookingForm.collaborator_ids?.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Aucun collaborateur sélectionné = attribution automatique
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Motif / Notes</Label>
                  <Input
                    value={bookingForm.reason}
                    onChange={(e) => setBookingForm({ ...bookingForm, reason: e.target.value })}
                    placeholder="Ex: Intervention urgente, rattrapage..."
                  />
                </div>
                {(() => {
                  const selectedSlotType = slotTypes.find(st => st.code === bookingForm.slot_type);
                  const requiredCount = selectedSlotType?.required_collaborators || 1;
                  const selectedCount = bookingForm.collaborator_ids?.length || 0;
                  const isValidCollaboratorCount = requiredCount === 1 || selectedCount === requiredCount;
                  
                  return (
                    <Button 
                      className="w-full rounded-full" 
                      onClick={handleCreateBookingForClient}
                      disabled={isSubmittingBooking || (requiredCount > 1 && !isValidCollaboratorCount)}
                    >
                      {isSubmittingBooking ? 'Création...' : (
                        requiredCount > 1 && !isValidCollaboratorCount 
                          ? `Sélectionnez ${requiredCount} collaborateurs`
                          : 'Créer la réservation'
                      )}
                    </Button>
                  );
                })()}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between bg-card rounded-xl p-3 md:p-4 shadow-sm gap-3">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="icon" onClick={() => handleNavigate('PREV')}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => handleNavigate('NEXT')}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleNavigate('TODAY')}>
            Aujourd'hui
          </Button>
        </div>
        <div className="text-center flex-1 min-w-0">
          <h2 className="text-base md:text-xl font-heading font-semibold capitalize truncate">
            {view === 'day' || viewMode === 'team'
              ? format(currentDate, 'EEEE d MMMM yyyy', { locale: fr })
              : view === 'week'
              ? `Semaine du ${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMMM', { locale: fr })}`
              : format(currentDate, 'MMMM yyyy', { locale: fr })
            }
          </h2>
          <Badge variant="outline" className="mt-1">
            Semaine {getWeek(currentDate, { weekStartsOn: 1, locale: fr })}
          </Badge>
        </div>
        <div className="flex items-center gap-3 text-xs md:text-sm flex-wrap justify-center lg:justify-end">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0"></div>
            <span>Abonnement</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-violet-500 flex-shrink-0"></div>
            <span>Ponctuel</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0"></div>
            <span>Devis</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full border-2 border-dashed border-orange-400 bg-orange-100 flex-shrink-0"></div>
            <span>À valider</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 border-2 border-cyan-600 flex-shrink-0"></div>
            <span>En cours</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></div>
            <span>Férié/Fermé</span>
          </div>
        </div>
      </div>


      {/* NEW: Members View - Rows per member, columns per day */}
      {viewMode === 'members' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Grid3X3 className="w-5 h-5 text-primary" />
                  Vue Membres - Semaine du {format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMMM yyyy', { locale: fr })}
                </CardTitle>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={expandAllMembers} className="text-xs">
                    Tout déplier
                  </Button>
                  <Button variant="ghost" size="sm" onClick={collapseAllMembers} className="text-xs">
                    Tout replier
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {/* Team total occupancy */}
                {(() => {
                  const teamOccupancy = getTeamOccupancy();
                  return (
                    <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-muted/30">
                      <div className="text-sm">
                        <span className="text-muted-foreground">Équipe :</span>
                        <span className={`ml-2 font-bold ${
                          teamOccupancy.color === 'green' ? 'text-green-600' : 
                          teamOccupancy.color === 'yellow' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {teamOccupancy.rate}% occupé
                        </span>
                      </div>
                      <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            teamOccupancy.color === 'green' ? 'bg-green-500' : 
                            teamOccupancy.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(teamOccupancy.rate, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {teamOccupancy.bookedHours}h / {teamOccupancy.maxHours}h
                      </span>
                    </div>
                  );
                })()}
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentDate(addDays(currentDate, -7))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
                    Aujourd'hui
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentDate(addDays(currentDate, 7))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {collaborators.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun collaborateur actif. Ajoutez des collaborateurs dans la section "Collaborateurs".
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse min-w-[900px]">
                  {/* Header with days */}
                  <thead className="bg-muted/30 sticky top-0 z-20">
                    <tr>
                      <th className="text-left p-3 font-semibold w-64 border-r bg-muted/50">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          Membre / Occupation
                        </div>
                      </th>
                      {getWeekDays(currentDate).map(day => {
                        const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                        const holiday = getHolidayForDate(day);
                        const closure = getClosureForDate(day);
                        const isClosed = holiday || closure || !isWorkingDay(day);
                        const dayOccupancy = getTeamDayOccupancy(day);
                        const lunchBreak = getLunchBreak(day);
                        
                        return (
                          <th 
                            key={format(day, 'yyyy-MM-dd')} 
                            className={`text-center p-2 border-r last:border-r-0 min-w-[120px] ${
                              isClosed ? 'bg-red-50 text-red-600' : 
                              isToday ? 'bg-primary/10' : ''
                            }`}
                          >
                            <div className="text-xs uppercase font-medium">{format(day, 'EEE', { locale: fr })}</div>
                            <div className={`text-lg font-bold ${isToday ? 'text-primary' : ''}`}>
                              {format(day, 'd')}
                            </div>
                            <div className="text-[10px] text-muted-foreground">{format(day, 'MMM', { locale: fr })}</div>
                            {holiday && <div className="text-[9px] text-red-500 truncate">{holiday.name}</div>}
                            {closure && !holiday && <div className="text-[9px] text-red-500 truncate">{closure.reason || 'Fermeture'}</div>}
                            {!holiday && !closure && !isWorkingDay(day) && <div className="text-[9px] text-red-500 font-medium">Fermé</div>}
                            
                            {/* Day occupancy indicator */}
                            {!isClosed && (
                              <div className="mt-1 flex flex-col items-center gap-0.5">
                                <div className="w-full max-w-[60px] h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${
                                      dayOccupancy.color === 'green' ? 'bg-green-500' : 
                                      dayOccupancy.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${Math.min(dayOccupancy.rate, 100)}%` }}
                                  />
                                </div>
                                <span className="text-[9px] text-muted-foreground">
                                  {dayOccupancy.freeCollaborators}/{dayOccupancy.totalCollaborators} dispo
                                </span>
                              </div>
                            )}
                            
                            {/* Lunch break indicator */}
                            {lunchBreak && !isClosed && (
                              <div className="text-[8px] text-orange-500 mt-0.5">
                                🍽️ {lunchBreak.start}-{lunchBreak.end}
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Row for unassigned / pending bookings */}
                    <tr className="border-t bg-amber-50/50">
                      <td className="p-3 border-r">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-amber-700">En attente</div>
                            <div className="text-xs text-amber-600">Réservations à valider</div>
                          </div>
                        </div>
                      </td>
                      {getWeekDays(currentDate).map(day => {
                        const pendingEvents = events.filter(e => 
                          (e.status === 'pending' || e.status === 'pending_validation' || e.isPendingValidation) &&
                          !e.collaborator_id &&
                          format(e.start, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')
                        );
                        const isClosed = getHolidayForDate(day) || getClosureForDate(day) || !isWorkingDay(day);
                        
                        return (
                          <td 
                            key={format(day, 'yyyy-MM-dd')} 
                            className={`p-2 border-r last:border-r-0 align-top ${isClosed ? 'bg-red-50/50' : ''}`}
                          >
                            {pendingEvents.length > 0 ? (
                              <div className="space-y-1">
                                {pendingEvents.slice(0, 3).map(event => (
                                  <div 
                                    key={event.id}
                                    onClick={() => setSelectedEvent(event)}
                                    data-testid={`pending-event-${event.id}`}
                                    className="p-1.5 bg-amber-100 border border-dashed border-amber-400 rounded text-xs cursor-pointer hover:shadow-md transition-shadow"
                                  >
                                    <div className="font-medium text-amber-800 truncate">{event.title}</div>
                                    <div className="text-amber-600">{format(event.start, 'HH:mm')}</div>
                                  </div>
                                ))}
                                {pendingEvents.length > 3 && (
                                  <div className="text-[10px] text-amber-600 text-center">
                                    +{pendingEvents.length - 3} autres
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center text-muted-foreground text-xs py-2">-</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    
                    {/* Rows for each collaborator */}
                    {collaborators.map(collab => {
                      const occupancy = getCollaboratorOccupancy(collab.id);
                      const isCollapsed = collapsedMembers[collab.id];
                      
                      return (
                        <tr key={collab.id} className="border-t hover:bg-muted/5">
                          {/* Member info cell */}
                          <td className="p-3 border-r bg-muted/10">
                            <div 
                              className="flex items-center gap-3 cursor-pointer"
                              onClick={() => toggleMemberCollapse(collab.id)}
                            >
                              {/* Collapse toggle */}
                              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                                {isCollapsed ? (
                                  <ChevronRight className="w-4 h-4" />
                                ) : (
                                  <ChevronLeft className="w-4 h-4 rotate-90" />
                                )}
                              </Button>
                              
                              {/* Avatar */}
                              {collab.avatar_url ? (
                                <img 
                                  src={getAssetUrl(collab.avatar_url)}
                                  alt={collab.name}
                                  className="w-10 h-10 rounded-full object-cover border-2"
                                  style={{ borderColor: occupancy.color === 'green' ? '#22c55e' : occupancy.color === 'yellow' ? '#eab308' : '#ef4444' }} loading="lazy" decoding="async"
                                />
                              ) : (
                                <div 
                                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold border-2"
                                  style={{ 
                                    backgroundColor: collab.color || '#6366f1',
                                    borderColor: occupancy.color === 'green' ? '#22c55e' : occupancy.color === 'yellow' ? '#eab308' : '#ef4444'
                                  }}
                                >
                                  {collab.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                              
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate flex items-center gap-2">
                                  {collab.name}
                                  {collab.role === 'commercial' && (
                                    <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1">Com.</Badge>
                                  )}
                                </div>
                                
                                {/* Occupancy bar */}
                                <div className="flex items-center gap-2 mt-1">
                                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden max-w-[100px]">
                                    <div 
                                      className={`h-full transition-all ${
                                        occupancy.color === 'green' ? 'bg-green-500' : 
                                        occupancy.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${Math.min(occupancy.rate, 100)}%` }}
                                    />
                                  </div>
                                  <span className={`text-xs font-medium ${
                                    occupancy.color === 'green' ? 'text-green-600' : 
                                    occupancy.color === 'yellow' ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {occupancy.rate}%
                                  </span>
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                  {occupancy.bookedHours}h / {occupancy.maxHours}h • {occupancy.label}
                                </div>
                              </div>
                            </div>
                          </td>
                          
                          {/* Day cells */}
                          {getWeekDays(currentDate).map(day => {
                            const dayEvents = getCollaboratorDayEvents(collab.id, day);
                            const isClosed = getHolidayForDate(day) || getClosureForDate(day) || !isWorkingDay(day);
                            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                            const collabLeave = getCollaboratorLeaveForDate(collab.id, day);
                            const isOnLeave = !!collabLeave;
                            
                            // Get leave type label
                            const getLeaveLabel = (type) => {
                              const types = {
                                vacation: 'Congés',
                                sick: 'Maladie',
                                training: 'Formation',
                                personal: 'Personnel',
                                other: 'Absence'
                              };
                              return types[type] || 'Congés';
                            };
                            
                            return (
                              <td 
                                key={format(day, 'yyyy-MM-dd')} 
                                className={`p-1 border-r last:border-r-0 align-top relative ${
                                  isOnLeave ? 'bg-green-50/70' :
                                  isClosed ? 'bg-red-50/50' : 
                                  isToday ? 'bg-primary/5' : ''
                                } ${!isClosed && !isOnLeave && dayEvents.length === 0 ? 'hover:bg-primary/10 cursor-pointer group' : ''}`}
                                onClick={() => {
                                  if (!isClosed && !isOnLeave && dayEvents.length === 0) {
                                    setBookingForm({
                                      ...bookingForm,
                                      date: format(day, 'yyyy-MM-dd'),
                                      collaborator_ids: [collab.id], // Pre-select this collaborator
                                      slot_type: 'half_day'
                                    });
                                    setIsBookingDialogOpen(true);
                                  }
                                }}
                              >
                                {isOnLeave ? (
                                  <div className="text-center py-4">
                                    <div className="flex flex-col items-center gap-1">
                                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                        <span className="text-lg">🌴</span>
                                      </div>
                                      <span className="text-[10px] font-medium text-green-700">
                                        {getLeaveLabel(collabLeave.leave_type)}
                                      </span>
                                      {collabLeave.reason && (
                                        <span className="text-[9px] text-green-600 truncate max-w-full px-1">
                                          {collabLeave.reason}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : isClosed ? (
                                  <div className="text-center py-4">
                                    <X className="w-4 h-4 text-red-400 mx-auto" />
                                  </div>
                                ) : isCollapsed ? (
                                  /* Collapsed view - just show count */
                                  <div className="text-center py-2">
                                    {dayEvents.length > 0 ? (
                                      <Badge 
                                        variant="secondary" 
                                        className="cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleMemberCollapse(collab.id);
                                        }}
                                      >
                                        {dayEvents.length} RDV
                                      </Badge>
                                    ) : (
                                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Plus className="w-4 h-4 text-primary mx-auto" />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  /* Expanded view - show detailed hourly timeline */
                                  <div className="min-h-[200px] relative">
                                    {/* Hourly grid */}
                                    <div className="space-y-0">
                                      {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(hour => {
                                        const hourStr = `${hour.toString().padStart(2, '0')}:00`;
                                        const isLunchHour = hour === 12 || hour === 13;
                                        
                                        // Find events that cover this hour
                                        const hourEvents = dayEvents.filter(event => {
                                          const eventStartHour = event.start.getHours();
                                          const eventEndHour = event.end.getHours();
                                          return eventStartHour <= hour && eventEndHour > hour;
                                        });
                                        
                                        // Check if this is the start hour of an event
                                        const startingEvents = dayEvents.filter(event => 
                                          event.start.getHours() === hour
                                        );
                                        
                                        return (
                                          <div 
                                            key={hour} 
                                            className={`flex items-stretch border-t first:border-t-0 ${
                                              isLunchHour ? 'bg-orange-50/50' : ''
                                            } ${hourEvents.length === 0 && !isLunchHour ? 'hover:bg-primary/5 cursor-pointer' : ''} ${
                                              dropTarget?.day && format(dropTarget.day, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd') && 
                                              dropTarget?.hour === hour && dropTarget?.collabId === collab.id 
                                                ? 'bg-primary/20 ring-2 ring-primary ring-inset' : ''
                                            }`}
                                            onClick={() => {
                                              if (hourEvents.length === 0 && !isLunchHour && !draggedEvent) {
                                                setBookingForm({
                                                  ...bookingForm,
                                                  date: format(day, 'yyyy-MM-dd'),
                                                  collaborator_ids: [collab.id],
                                                  slot_type: 'half_day'
                                                });
                                                setIsBookingDialogOpen(true);
                                              }
                                            }}
                                            onDragOver={(e) => handleDragOver(e, day, hour, collab.id)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, day, hour, collab.id)}
                                          >
                                            {/* Hour label */}
                                            <div className="w-10 text-[9px] text-muted-foreground py-1 px-1 border-r bg-muted/20 flex-shrink-0">
                                              {hourStr}
                                            </div>
                                            
                                            {/* Content area */}
                                            <div className="flex-1 min-h-[20px] relative">
                                              {isLunchHour && hourEvents.length === 0 && (
                                                <div className="text-[8px] text-orange-400 px-1 py-0.5">
                                                  {hour === 12 ? '🍽️ Pause' : ''}
                                                </div>
                                              )}
                                              
                                              {startingEvents.map(event => {
                                                const duration = (event.end - event.start) / (1000 * 60 * 60);
                                                const heightPx = Math.max(duration * 20, 18);
                                                
                                                const bgColor = event.type === 'quote' 
                                                  ? 'bg-amber-200 border-amber-400 text-amber-900'
                                                  : event.type === 'punctual'
                                                  ? 'bg-violet-200 border-violet-400 text-violet-900'
                                                  : event.isPendingValidation
                                                  ? 'bg-orange-200 border-orange-400 border-dashed text-orange-900'
                                                  : 'bg-blue-200 border-blue-400 text-blue-900';
                                                
                                                return (
                                                  <div 
                                                    key={event.id}
                                                    draggable={true}
                                                    onDragStart={(e) => handleDragStart(e, event)}
                                                    onDragEnd={handleDragEnd}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setSelectedEvent(event);
                                                    }}
                                                    className={`absolute left-0 right-1 ${bgColor} border rounded text-[9px] px-1 py-0.5 cursor-grab active:cursor-grabbing hover:shadow-lg transition-shadow z-10 overflow-hidden`}
                                                    style={{ 
                                                      height: `${heightPx}px`,
                                                      top: '0px'
                                                    }}
                                                    data-testid={`planning-event-${event.id}`}
                                                  >
                                                    <div className="font-medium truncate flex items-center gap-1">
                                                      {isRecentlyModified(event.resource) && (
                                                        <RotateCcw
                                                          className="w-3 h-3 text-amber-600 flex-shrink-0"
                                                          aria-label="Récemment déplacé"
                                                          title={recentlyModifiedTooltip(event.resource)}
                                                          data-testid={`planning-event-${event.id}-recently-moved`}
                                                        />
                                                      )}
                                                      <span className="truncate">{event.title}</span>
                                                    </div>
                                                    <div className="opacity-75 truncate">
                                                      {format(event.start, 'HH:mm')}-{format(event.end, 'HH:mm')}
                                                    </div>
                                                    {event.isPendingValidation && (
                                                      <span className="text-[7px] bg-orange-300 px-1 rounded">À valider</span>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    
                                    {/* Empty state with + button */}
                                    {dayEvents.length === 0 && (
                                      <div 
                                        className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                                        onClick={() => {
                                          setBookingForm({
                                            ...bookingForm,
                                            date: format(day, 'yyyy-MM-dd'),
                                            collaborator_ids: [collab.id],
                                            slot_type: 'half_day'
                                          });
                                          setIsBookingDialogOpen(true);
                                        }}
                                      >
                                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                          <Plus className="w-5 h-5 text-primary" />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* Team View - Google Agenda Style */}
      {viewMode === 'team' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Planning Équipe - Semaine du {format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMMM yyyy', { locale: fr })}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(addDays(currentDate, -7))}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
                  Aujourd'hui
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(addDays(currentDate, 7))}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {collaborators.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucun collaborateur actif. Ajoutez des collaborateurs dans la section "Collaborateurs".
              </p>
            ) : (
              <div className="overflow-x-auto">
                {/* Vue par jour de la semaine - Lundi à Samedi */}
                {getWeekDays(currentDate).map(day => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                  const dayEvents = events.filter(e => format(e.start, 'yyyy-MM-dd') === dayStr);
                  const holiday = getHolidayForDate(day);
                  const closure = getClosureForDate(day);
                  const isClosed = holiday || closure || !isWorkingDay(day);
                  
                  return (
                    <div key={dayStr} className={`border-b last:border-b-0 ${isClosed ? 'opacity-60' : ''}`}>
                      {/* En-tête du jour */}
                      <div className={`flex items-center justify-between p-3 ${
                        isClosed ? 'bg-red-50 dark:bg-red-900/20' :
                        isToday ? 'bg-primary/10' : 'bg-muted/30'
                      }`}>
                        <div className="flex items-center gap-3">
                          <div className={`text-center min-w-[60px] ${isToday && !isClosed ? 'text-primary' : ''} ${isClosed ? 'text-red-500' : ''}`}>
                            <div className="text-xs uppercase font-medium">{format(day, 'EEE', { locale: fr })}</div>
                            <div className={`text-2xl font-bold ${
                              isClosed ? 'bg-red-500 text-white rounded-full w-10 h-10 flex items-center justify-center mx-auto' :
                              isToday ? 'bg-primary text-white rounded-full w-10 h-10 flex items-center justify-center mx-auto' : ''
                            }`}>
                              {format(day, 'd')}
                            </div>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {format(day, 'MMMM yyyy', { locale: fr })}
                          </div>
                          {holiday && (
                            <Badge variant="destructive" className="text-xs flex items-center gap-1">
                              <Star className="w-3 h-3" />
                              {holiday.name}
                            </Badge>
                          )}
                          {closure && !holiday && (
                            <Badge variant="destructive" className="text-xs">
                              Fermé : {closure.reason || 'Fermeture exceptionnelle'}
                            </Badge>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {isClosed ? 'Fermé' : `${dayEvents.length} RDV`}
                        </Badge>
                      </div>
                      
                      {/* Grille horaire avec colonnes par collaborateur */}
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse min-w-[800px]">
                          <thead>
                            <tr className="bg-muted/20">
                              <th className="text-left p-2 text-xs font-semibold w-16 border-r">Heure</th>
                              {/* Colonne "Non assigné" pour les réservations en attente */}
                              <th className="text-center p-2 text-xs font-semibold border-r bg-amber-50">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                                    <Clock className="w-3 h-3 text-amber-600" />
                                  </div>
                                  <span className="text-amber-700">En attente</span>
                                </div>
                              </th>
                              {collaborators.map(collab => (
                                <th key={collab.id} className="text-center p-2 text-xs font-semibold border-r last:border-r-0">
                                  <div className="flex items-center justify-center gap-2">
                                    {collab.avatar_url ? (
                                      <img 
                                        src={getAssetUrl(collab.avatar_url)}
                                        alt={collab.name}
                                        className="w-6 h-6 rounded-full object-cover" loading="lazy" decoding="async"
                                      />
                                    ) : (
                                      <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                                        <User className="w-3 h-3 text-primary" />
                                      </div>
                                    )}
                                    <span className="truncate max-w-[100px]">{collab.name}</span>
                                    {collab.role === 'commercial' && (
                                      <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1">Com.</Badge>
                                    )}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {WORK_HOURS.map(hour => (
                              <tr key={hour} className="border-t hover:bg-muted/10">
                                <td className="p-1 text-xs text-muted-foreground border-r text-center font-medium bg-muted/5">
                                  {hour}h
                                </td>
                                {/* Colonne "Non assigné" - réservations sans collaborateur */}
                                <td className="p-1 border-r h-12 align-top relative bg-amber-50/30">
                                  {getEventsForHour(null, day, hour).map(event => {
                                    if (!isEventStart(event, hour)) return null;
                                    
                                    const duration = getEventDuration(event);
                                    const isPending = event.isPendingValidation || event.status === 'pending_validation';
                                    
                                    return (
                                      <div
                                        key={event.id}
                                        onClick={() => setSelectedEvent(event)}
                                        className={`absolute inset-x-1 ${isPending ? 'bg-amber-100 border-amber-400 text-amber-800 border-dashed border-2' : 'bg-gray-100 border-gray-300 text-gray-700'} border rounded-md p-1 cursor-pointer hover:shadow-md transition-shadow overflow-hidden z-10`}
                                        style={{ 
                                          height: `${duration * 48 - 4}px`,
                                          minHeight: '44px'
                                        }}
                                        data-testid={`planning-event-${event.id}`}
                                      >
                                        <div className="text-xs font-medium truncate flex items-center gap-1">
                                          {isRecentlyModified(event.resource) && (
                                            <RotateCcw className="w-3 h-3 text-amber-600 flex-shrink-0" aria-label="Récemment déplacé" title={recentlyModifiedTooltip(event.resource)} />
                                          )}
                                          <span className="truncate">{isPending && '⏳ '}{event.title}</span>
                                        </div>
                                        <div className="text-[10px] opacity-75">
                                          {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
                                        </div>
                                        {isPending && (
                                          <Badge className="bg-amber-200 text-amber-800 text-[9px] px-1 mt-0.5">À valider</Badge>
                                        )}
                                      </div>
                                    );
                                  })}
                                </td>
                                {collaborators.map(collab => {
                                  const hourEvents = getEventsForHour(collab.id, day, hour);
                                  const hasEvents = hourEvents.length > 0;
                                  const isClosed = getHolidayForDate(day) || getClosureForDate(day) || !isWorkingDay(day);
                                  
                                  return (
                                    <td 
                                      key={collab.id} 
                                      className={`p-1 border-r last:border-r-0 h-12 align-top relative ${
                                        !hasEvents && !isClosed ? 'hover:bg-primary/5 cursor-pointer group' : ''
                                      }`}
                                      onClick={() => {
                                        if (!hasEvents && !isClosed) {
                                          // Open booking dialog with pre-filled data
                                          setBookingForm({
                                            ...bookingForm,
                                            date: format(day, 'yyyy-MM-dd'),
                                            collaborator_ids: [collab.id], // Pre-select this collaborator
                                            slot_type: 'half_day'
                                          });
                                          setIsBookingDialogOpen(true);
                                        }
                                      }}
                                    >
                                      {/* Show "+" icon on hover for empty slots */}
                                      {!hasEvents && !isClosed && (
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Plus className="w-4 h-4 text-primary" />
                                          </div>
                                        </div>
                                      )}
                                      {hourEvents.map(event => {
                                        if (!isEventStart(event, hour)) return null;
                                        
                                        const duration = getEventDuration(event);
                                        const bgColor = event.type === 'quote' 
                                          ? 'bg-amber-100 border-amber-300 text-amber-800'
                                          : event.type === 'punctual'
                                          ? 'bg-blue-100 border-blue-300 text-blue-800'
                                          : 'bg-green-100 border-green-300 text-green-800';
                                        
                                        return (
                                          <div
                                            key={event.id}
                                            onClick={() => setSelectedEvent(event)}
                                            className={`absolute inset-x-1 ${bgColor} border rounded-md p-1 cursor-pointer hover:shadow-md transition-shadow overflow-hidden z-10`}
                                            style={{ 
                                              height: `${duration * 48 - 4}px`,
                                              minHeight: '44px'
                                            }}
                                            data-testid={`planning-event-${event.id}`}
                                          >
                                            <div className="text-xs font-medium truncate flex items-center gap-1">
                                              {isRecentlyModified(event.resource) && (
                                                <RotateCcw className="w-3 h-3 text-amber-600 flex-shrink-0" aria-label="Récemment déplacé" title={recentlyModifiedTooltip(event.resource)} />
                                              )}
                                              <span className="truncate">{event.title}</span>
                                            </div>
                                            <div className="text-[10px] opacity-75">
                                              {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
                                            </div>
                                            {event.type === 'quote' && (
                                              <Badge className="bg-amber-200 text-amber-800 text-[9px] px-1 mt-0.5">Devis</Badge>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card className="overflow-hidden print:shadow-none" ref={printRef}>
          <CardContent className="p-0">
            <div className="px-4 pt-3 pb-1 flex items-center gap-2 text-xs text-muted-foreground border-b bg-muted/30" data-testid="planning-dnd-hint">
              <Info className="w-3.5 h-3.5" />
              <span><strong>Astuce :</strong> glissez un événement pour le déplacer, tirez sur son bord pour le redimensionner, ou maintenez <kbd className="px-1 py-0.5 rounded border bg-background text-[10px] font-mono">Alt</kbd> pendant le glissement pour le dupliquer.</span>
            </div>
            <div className="h-[600px] p-4" id="calendar-container">
              <DnDCalendar
                localizer={localizer}
                events={filteredEvents}
                startAccessor="start"
                endAccessor="end"
                view={view}
                onView={setView}
                date={currentDate}
                onNavigate={setCurrentDate}
                messages={messages}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={handleSelectEvent}
                popup
                selectable
                culture="fr"
                toolbar={false}
                resizable
                draggableAccessor={isEventDraggable}
                resizableAccessor={isEventDraggable}
                onEventDrop={handleCalendarEventDrop}
                onEventResize={handleCalendarEventResize}
                data-testid="planning-calendar"
                formats={{
                  dayFormat: (date, culture, localizer) => format(date, 'EEE d', { locale: fr }),
                  weekdayFormat: (date, culture, localizer) => format(date, 'EEEE', { locale: fr }),
                  monthHeaderFormat: (date, culture, localizer) => format(date, 'MMMM yyyy', { locale: fr }),
                  dayHeaderFormat: (date, culture, localizer) => format(date, 'EEEE d MMMM', { locale: fr }),
                  dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                    `${format(start, 'd', { locale: fr })} - ${format(end, 'd MMMM yyyy', { locale: fr })}`,
                  agendaDateFormat: (date, culture, localizer) => format(date, 'EEE d MMM', { locale: fr }),
                  agendaTimeFormat: (date, culture, localizer) => format(date, 'HH:mm', { locale: fr }),
                  agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
                    `${format(start, 'HH:mm', { locale: fr })} - ${format(end, 'HH:mm', { locale: fr })}`,
                }}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Event Detail Modal */}
      <Dialog open={!!selectedEvent} onOpenChange={() => { setSelectedEvent(null); setIsEditMode(false); setRejectionReason(''); }}>
        <DialogContent className="max-w-lg w-[calc(100%-1rem)] max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {selectedEvent?.type === 'availability' ? 'Disponibilité' : 
               selectedEvent?.type === 'punctual' ? 'Réservation Ponctuelle' : 'Réservation Abonnement'}
              {selectedEvent?.status === 'pending' && (
                <Badge variant="outline" className="bg-orange-100 text-orange-700 ml-2">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  En attente
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent?.status === 'pending' 
                ? 'Cette réservation nécessite votre validation'
                : selectedEvent?.type === 'availability' 
                  ? 'Détails de la disponibilité'
                  : 'Détails de la réservation'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && !isEditMode && (
            <div className="space-y-4 mt-4">
              {/* Date & Time */}
              <div className="flex items-center gap-2 text-muted-foreground">
                <CalendarIcon className="w-4 h-4" />
                <span>{format(selectedEvent.start, 'EEEE d MMMM yyyy', { locale: fr })}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>{format(selectedEvent.start, 'HH:mm')} - {format(selectedEvent.end, 'HH:mm')}</span>
              </div>
              
              {/* Punctual Booking Details */}
              {selectedEvent.type === 'punctual' && (
                <>
                  {/* Client Info Card */}
                  <div className="p-4 bg-muted/50 rounded-xl border space-y-3">
                    <h4 className="font-semibold text-sm uppercase text-muted-foreground">Informations Client</h4>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{selectedEvent.resource?.client_name}</p>
                        {selectedEvent.resource?.client_email && (
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Mail className="w-3 h-3" />
                            <span>{selectedEvent.resource?.client_email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedEvent.resource?.client_phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <a href={`tel:${selectedEvent.resource.client_phone}`} className="text-primary hover:underline">
                          {selectedEvent.resource.client_phone}
                        </a>
                      </div>
                    )}
                    {selectedEvent.resource?.created_at && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1" data-testid="booking-created-at">
                        <Clock className="w-3 h-3" />
                        Créée le {new Date(selectedEvent.resource.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </div>
                  
                  {/* Service Info */}
                  <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
                    <p className="text-sm text-muted-foreground">Service demandé</p>
                    <p className="font-semibold text-lg">{selectedEvent.resource?.service_name}</p>
                    <p className="text-2xl font-bold text-primary mt-1">
                      {selectedEvent.resource?.total_price?.toFixed(2)}€
                    </p>
                    {selectedEvent.resource?.duration_minutes && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Durée : {Math.floor(selectedEvent.resource.duration_minutes / 60)}h{selectedEvent.resource.duration_minutes % 60 > 0 ? ` ${selectedEvent.resource.duration_minutes % 60}min` : ''}
                      </p>
                    )}
                  </div>
                  
                  {/* Notes if any */}
                  {selectedEvent.resource?.notes && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm font-medium text-amber-800">Notes du client :</p>
                      <p className="text-sm text-amber-700">{selectedEvent.resource.notes}</p>
                    </div>
                  )}
                  
                  {/* Collaborator if assigned */}
                  {selectedEvent.collaborator && (
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>Assigné à : <strong>{selectedEvent.collaborator}</strong></span>
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Statut :</span>
                    <Badge variant={selectedEvent.status === 'pending' ? 'outline' : selectedEvent.status === 'confirmed' ? 'default' : 'destructive'}
                           className={selectedEvent.status === 'pending' ? 'bg-orange-100 text-orange-700' : selectedEvent.status === 'confirmed' ? 'bg-green-500' : ''}>
                      {selectedEvent.status === 'pending' ? 'En attente de validation' : 
                       selectedEvent.status === 'confirmed' ? 'Confirmé' : 'Annulé'}
                    </Badge>
                  </div>
                  
                  {/* Assigned Collaborators */}
                  {(selectedEvent.resource?.collaborator_ids?.length > 0 || selectedEvent.collaborator) && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Collaborateurs assignés</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedEvent.resource?.collaborator_ids?.length > 0 ? (
                          selectedEvent.resource.collaborator_ids.map(collabId => {
                            const collab = collaborators.find(c => c.id === collabId);
                            return collab ? (
                              <Badge key={collabId} variant="secondary" className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {collab.name}
                              </Badge>
                            ) : null;
                          })
                        ) : selectedEvent.collaborator ? (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {selectedEvent.collaborator}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons for Pending */}
                  {selectedEvent.status === 'pending' && (
                    <div className="space-y-3 pt-4 border-t">
                      {/* Assign Collaborator */}
                      <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                        <label className="text-sm font-medium">Assigner à un membre</label>
                        <Select
                          value={selectedEvent.collaborator_id || ''}
                          onValueChange={(value) => {
                            setSelectedEvent({
                              ...selectedEvent,
                              collaborator_id: value,
                              collaborator_name: collaborators.find(c => c.id === value)?.name || ''
                            });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choisir un collaborateur..." />
                          </SelectTrigger>
                          <SelectContent>
                            {collaborators.map(collab => (
                              <SelectItem key={collab.id} value={collab.id}>
                                {collab.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          className="flex-1"
                          onClick={openEditMode}
                          data-testid="booking-edit-btn"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleValidateBooking(selectedEvent.resource?.id, selectedEvent.collaborator_id)}
                          disabled={isProcessing}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Valider
                        </Button>
                        <Button 
                          variant="destructive" 
                          className="flex-1"
                          onClick={() => handleRejectBooking(selectedEvent.resource?.id)}
                          disabled={isProcessing}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons for Confirmed - Show "Démarrer" */}
                  {selectedEvent.status === 'confirmed' && (
                    <div className="space-y-3 pt-4 border-t">
                      {/* Start with photo button */}
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => setShowStartDialog(true)}
                        disabled={isProcessing}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Démarrer la prestation
                      </Button>
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={openEditMode}
                          data-testid="booking-edit-btn"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => setShowDuplicateDialog(true)}
                          data-testid="booking-duplicate-btn"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Dupliquer
                        </Button>
                        <Button 
                          variant="destructive" 
                          className="flex-1"
                          onClick={() => handleCancelBooking(selectedEvent.resource?.id, 'punctual')}
                          disabled={isProcessing}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons for In Progress - Show "Terminer" */}
                  {selectedEvent.status === 'in_progress' && (
                    <div className="space-y-3 pt-4 border-t">
                      <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Prestation en cours...
                        </p>
                      </div>
                      
                      {/* Complete with photo button */}
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => setShowCompleteDialog(true)}
                        disabled={isProcessing}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Terminer la prestation
                      </Button>
                    </div>
                  )}
                </>
              )}
              
              {/* Subscription Booking Details */}
              {selectedEvent.type === 'booking' && (
                <>
                  <div className={`p-4 rounded-xl border ${selectedEvent.isPendingValidation ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                    <p className={`text-sm ${selectedEvent.isPendingValidation ? 'text-amber-600' : 'text-blue-600'}`}>
                      {selectedEvent.isPendingValidation ? '⏳ Réservation en attente de validation' : 'Réservation Abonnement'}
                    </p>
                    <p className="font-semibold">{selectedEvent.resource?.client_name || selectedEvent.title}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {(() => {
                        const rawSlot = selectedEvent.resource?.slot_type;
                        if (!rawSlot) return '—';
                        if (rawSlot === 'full_day') return 'Journée complète';
                        if (rawSlot === 'half_day') return 'Demi-journée';
                        // Custom slot type — resolve name from slotTypes list
                        const st = slotTypes.find(s => s.code === rawSlot || s.id === rawSlot);
                        return st?.name || rawSlot;
                      })()}
                      {selectedEvent.resource?.slot_period && ` - ${selectedEvent.resource.slot_period === 'morning' ? 'Matin' : selectedEvent.resource.slot_period === 'afternoon' ? 'Après-midi' : 'Journée'}`}
                    </p>
                  </div>
                  
                  {/* Collaborator assignment */}
                  {(selectedEvent.collaborator_name || selectedEvent.isPendingValidation) && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Collaborateur(s) assigné(s)</Label>
                      {selectedEvent.isPendingValidation ? (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground">
                            Cochez un ou plusieurs collaborateurs pour cette intervention
                          </p>
                          <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                            {collaborators.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-2">Aucun collaborateur</p>
                            ) : (
                              collaborators.map((collab) => {
                                const isChecked = editForm.collaborator_ids?.includes(collab.id);
                                return (
                                  <div 
                                    key={collab.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                      isChecked ? 'bg-primary/10 border border-primary' : 'hover:bg-muted/50'
                                    }`}
                                    onClick={() => {
                                      const currentIds = editForm.collaborator_ids || [];
                                      const newIds = isChecked 
                                        ? currentIds.filter(id => id !== collab.id)
                                        : [...currentIds, collab.id];
                                      setEditForm({ ...editForm, collaborator_ids: newIds });
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {}}
                                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    {collab.avatar_url ? (
                                      <img 
                                        src={getAssetUrl(collab.avatar_url)}
                                        alt={collab.name}
                                        className="w-7 h-7 rounded-full object-cover" loading="lazy" decoding="async"
                                      />
                                    ) : (
                                      <div 
                                        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium"
                                        style={{ backgroundColor: collab.color || '#6366f1' }}
                                      >
                                        {collab.name?.charAt(0)?.toUpperCase()}
                                      </div>
                                    )}
                                    <span className="text-sm font-medium">{collab.name}</span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                          {editForm.collaborator_ids?.length > 0 && (
                            <p className="text-xs text-primary font-medium">
                              {editForm.collaborator_ids.length} collaborateur{editForm.collaborator_ids.length > 1 ? 's' : ''} sélectionné{editForm.collaborator_ids.length > 1 ? 's' : ''}
                            </p>
                          )}
                          {editForm.collaborator_ids?.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                              Aucun collaborateur sélectionné
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span>Collaborateur : <strong>{selectedEvent.collaborator_name}</strong></span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Actions for pending validation */}
                  {selectedEvent.isPendingValidation && (
                    <div className="space-y-3 pt-4 border-t">
                      <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded">
                        Cette réservation a été planifiée par le client et nécessite votre validation.
                      </p>
                      {(() => {
                        const reqCount = selectedEvent.requiredCollaborators || 1;
                        const selCount = (editForm.collaborator_ids || []).length;
                        if (reqCount > 1) {
                          const ok = selCount === reqCount;
                          return (
                            <div
                              className={`text-sm p-2 rounded border ${
                                ok
                                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                  : 'bg-orange-50 border-orange-200 text-orange-800'
                              }`}
                              data-testid="multi-collab-hint"
                            >
                              👥 Ce créneau nécessite <strong>{reqCount} collaborateurs</strong>
                              {' '}({selCount}/{reqCount} sélectionnés)
                              {!ok && (
                                <span className="block mt-0.5 text-xs">
                                  Assignez {reqCount} collaborateurs avant de valider.
                                </span>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <div className="flex gap-2">
                        <Button 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            const collabIds = editForm.collaborator_ids || [];
                            const reqCount = selectedEvent.requiredCollaborators || 1;
                            if (reqCount > 1 && collabIds.length !== reqCount) {
                              toast.error(`Ce créneau requiert ${reqCount} collaborateurs (${collabIds.length} sélectionné${collabIds.length > 1 ? 's' : ''})`);
                              return;
                            }
                            
                            setIsProcessing(true);
                            try {
                              if (collabIds.length <= 1) {
                                // Single collaborator or none - validate with single call
                                await companyApi.validateBooking(selectedEvent.resource?.id, {
                                  collaborator_id: collabIds[0] || null
                                });
                                toast.success('Réservation validée !');
                              } else {
                                // Multiple collaborators - validate first, then duplicate for others
                                // First validation with first collaborator
                                await companyApi.validateBooking(selectedEvent.resource?.id, {
                                  collaborator_id: collabIds[0]
                                });
                                
                                // Create additional bookings for other collaborators
                                const bookingData = selectedEvent.resource;
                                for (let i = 1; i < collabIds.length; i++) {
                                  await companyApi.createBookingForClient({
                                    client_id: bookingData?.client_id,
                                    date: bookingData?.date || format(selectedEvent.start, 'yyyy-MM-dd'),
                                    slot_type: bookingData?.slot_type || 'half_day',
                                    reason: bookingData?.reason || 'Duplication depuis validation',
                                    collaborator_id: collabIds[i]
                                  });
                                }
                                toast.success(`Réservation validée et assignée à ${collabIds.length} collaborateurs !`);
                              }
                              setSelectedEvent(null);
                              setEditForm({ ...editForm, collaborator_ids: [] });
                              fetchData();
                            } catch (err) {
                              toast.error('Erreur lors de la validation');
                            } finally {
                              setIsProcessing(false);
                            }
                          }}
                          disabled={isProcessing || (selectedEvent.requiredCollaborators > 1 && (editForm.collaborator_ids || []).length !== selectedEvent.requiredCollaborators)}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          {editForm.collaborator_ids?.length > 1 
                            ? `Valider (${editForm.collaborator_ids.length} personnes)` 
                            : 'Valider'}
                        </Button>
                        <Button 
                          variant="destructive" 
                          className="flex-1"
                          onClick={async () => {
                            setIsProcessing(true);
                            try {
                              await companyApi.rejectBooking(selectedEvent.resource?.id, 'Refusé par l\'entreprise');
                              toast.success('Réservation refusée');
                              setSelectedEvent(null);
                              fetchData();
                            } catch (err) {
                              toast.error('Erreur lors du refus');
                            } finally {
                              setIsProcessing(false);
                            }
                          }}
                          disabled={isProcessing}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Actions for confirmed subscription bookings - Show "Démarrer" */}
                  {!selectedEvent.isPendingValidation && selectedEvent.resource?.status !== 'in_progress' && (
                    <div className="space-y-3 pt-4 border-t">
                      {/* Start with photo button for subscription bookings */}
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => setShowStartDialog(true)}
                        disabled={isProcessing}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Démarrer la prestation
                      </Button>
                      
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={openEditMode}
                          data-testid="booking-edit-btn"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                        <Button 
                          variant="destructive" 
                          className="flex-1"
                          onClick={() => handleCancelBooking(selectedEvent.resource?.id, 'subscription')}
                          disabled={isProcessing}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Actions for in_progress subscription bookings - Show "Terminer" */}
                  {!selectedEvent.isPendingValidation && selectedEvent.resource?.status === 'in_progress' && (
                    <div className="space-y-3 pt-4 border-t">
                      <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-700 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Prestation en cours...
                        </p>
                      </div>
                      
                      {/* Complete with photo button for subscription bookings */}
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={() => setShowCompleteDialog(true)}
                        disabled={isProcessing}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Terminer la prestation
                      </Button>
                    </div>
                  )}
                </>
              )}
              
              {/* Availability Details */}
              {selectedEvent.type === 'availability' && (
                <>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedEvent.collaborator}</span>
                  </div>
                  <Badge variant={selectedEvent.status === 'booked' ? 'secondary' : 'default'} 
                         className={selectedEvent.status === 'available' ? 'bg-green-500' : ''}>
                    {selectedEvent.status === 'booked' ? 'Déjà réservé' : 'Disponible'}
                  </Badge>
                  
                  {selectedEvent.status === 'available' && (
                    <Button 
                      variant="destructive" 
                      className="w-full"
                      onClick={() => handleDeleteAvailability(selectedEvent.resource?.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer cette disponibilité
                    </Button>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Edit Mode Form */}
          {selectedEvent && isEditMode && (
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              {/* Client Selection */}
              {(selectedEvent.type === 'punctual' || selectedEvent.type === 'booking') && (
                <div className="space-y-2">
                  <Label>Client</Label>
                  <div className="flex gap-2">
                    <Select
                      value={editForm.client_id}
                      onValueChange={(value) => setEditForm({ ...editForm, client_id: value })}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Sélectionner un client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name} - {c.email}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setIsQuickClientDialogOpen(true)}
                      title="Créer un nouveau client"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Date */}
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                />
              </div>
              
              {/* Slot Type */}
              <div className="space-y-2">
                <Label>Type de créneau</Label>
                <Select
                  value={editForm.slot_type}
                  onValueChange={(value) => setEditForm({ ...editForm, slot_type: value, slot_period: '' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un type" />
                  </SelectTrigger>
                  <SelectContent>
                    {slotTypes.filter(st => st.is_active !== false).map((st) => (
                      <SelectItem key={st.id || st.code} value={st.code || st.id}>
                        {st.name} ({st.duration_hours}h){st.required_collaborators > 1 ? ` - ${st.required_collaborators} pers.` : ''}
                      </SelectItem>
                    ))}
                    {slotTypes.length === 0 && (
                      <>
                        <SelectItem value="half_day">Demi-journée (4h)</SelectItem>
                        <SelectItem value="full_day">Journée complète (7h)</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Slot Period (for half-day) */}
              {editForm.slot_type === 'half_day' && (
                <div className="space-y-2">
                  <Label>Période</Label>
                  <Select
                    value={editForm.slot_period}
                    onValueChange={(value) => setEditForm({ ...editForm, slot_period: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une période" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Matin (8h-12h)</SelectItem>
                      <SelectItem value="afternoon">Après-midi (14h-18h)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Collaborators */}
              <div className="space-y-2">
                <Label>Collaborateurs assignés</Label>
                {(() => {
                  const selectedSlotType = slotTypes.find(st => (st.code || st.id) === editForm.slot_type);
                  const requiredCount = selectedSlotType?.required_collaborators || 1;
                  const selectedCount = editForm.collaborator_ids?.length || 0;
                  
                  return requiredCount > 1 && (
                    <p className={`text-xs mb-2 ${selectedCount !== requiredCount ? 'text-amber-600 font-medium' : 'text-green-600'}`}>
                      {selectedCount === requiredCount 
                        ? `✓ ${requiredCount} collaborateurs sélectionnés`
                        : `⚠️ Ce créneau nécessite ${requiredCount} collaborateurs (${selectedCount}/${requiredCount})`
                      }
                    </p>
                  );
                })()}
                <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-y-auto">
                  {collaborators.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun collaborateur disponible</p>
                  ) : (
                    collaborators.map(c => {
                      const avail = getCollabAvailabilityForEdit(
                        c.id,
                        editForm.date,
                        editForm.slot_period || editForm.slot_type,
                        selectedEvent?.resource?.id,
                      );
                      const isChecked = (editForm.collaborator_ids || []).includes(c.id);
                      const statusStyle = {
                        available: { dot: 'bg-emerald-500', pill: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Disponible' },
                        leave: { dot: 'bg-orange-500', pill: 'text-orange-700 bg-orange-50 border-orange-200', label: 'En congé' },
                        busy: { dot: 'bg-red-500', pill: 'text-red-700 bg-red-50 border-red-200', label: 'Occupé' },
                        closed: { dot: 'bg-amber-500', pill: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Fermeture' },
                      }[avail.status] || { dot: 'bg-gray-400', pill: 'text-gray-600 bg-gray-50 border-gray-200', label: 'N/A' };
                      const conflict = avail.status !== 'available';
                      return (
                        <label
                          key={c.id}
                          title={avail.reason || statusStyle.label}
                          className={`flex items-center gap-3 p-2 hover:bg-muted rounded-lg cursor-pointer ${
                            conflict && isChecked ? 'ring-1 ring-red-200 bg-red-50/40' : ''
                          }`}
                        >
                          <Checkbox
                            data-testid={`edit-collab-checkbox-${c.id}`}
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              const currentIds = editForm.collaborator_ids || [];
                              if (checked) {
                                setEditForm({ ...editForm, collaborator_ids: [...currentIds, c.id] });
                              } else {
                                setEditForm({ ...editForm, collaborator_ids: currentIds.filter(id => id !== c.id) });
                              }
                            }}
                          />
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="relative">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="w-4 h-4 text-primary" />
                              </div>
                              <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${statusStyle.dot}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{c.name}</p>
                              {c.email && <p className="text-xs text-muted-foreground truncate">{c.email}</p>}
                            </div>
                          </div>
                          <span
                            data-testid={`edit-collab-availability-${c.id}`}
                            className={`hidden sm:inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${statusStyle.pill}`}
                          >
                            {statusStyle.label}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                {(editForm.collaborator_ids || []).length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {(editForm.collaborator_ids || []).length} collaborateur(s) sélectionné(s)
                  </p>
                )}
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  placeholder="Notes internes..."
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </div>
              
              {/* Reschedule Reason - Only show if date is being changed */}
              {editForm.date !== format(selectedEvent.start, 'yyyy-MM-dd') && (
                <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Label className="text-amber-800 font-medium flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Motif de modification de date *
                  </Label>
                  <Select
                    value={editForm.reschedule_reason}
                    onValueChange={(value) => setEditForm({ ...editForm, reschedule_reason: value, reschedule_reason_custom: '' })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un motif..." />
                    </SelectTrigger>
                    <SelectContent>
                      {RESCHEDULE_REASONS.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>{reason.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editForm.reschedule_reason === 'other' && (
                    <Input
                      placeholder="Précisez le motif..."
                      value={editForm.reschedule_reason_custom}
                      onChange={(e) => setEditForm({ ...editForm, reschedule_reason_custom: e.target.value })}
                      className="mt-2"
                    />
                  )}
                  <p className="text-xs text-amber-700">
                    Ce motif sera enregistré dans l'historique de la réservation
                  </p>
                </div>
              )}
              
              <div className="flex gap-2 pt-4 sticky bottom-0 bg-background">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setIsEditMode(false)}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleUpdateBooking}
                  disabled={isProcessing}
                  data-testid="edit-booking-save-btn"
                >
                  {isProcessing ? 'Modification...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #calendar-container, #calendar-container * {
            visibility: visible;
          }
          #calendar-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
        
        .rbc-calendar {
          font-family: inherit;
        }
        .rbc-header {
          padding: 8px;
          font-weight: 600;
          background: #f8f9fa;
        }
        .rbc-today {
          background-color: rgba(47, 82, 51, 0.1);
        }
        .rbc-event {
          padding: 4px 8px;
        }
        .rbc-event-content {
          font-size: 12px;
        }
        .rbc-toolbar {
          display: none;
        }
        .rbc-month-view, .rbc-time-view {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
        .rbc-time-header-content {
          border-left: none;
        }
        .rbc-agenda-view table.rbc-agenda-table {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
        .rbc-agenda-view table.rbc-agenda-table thead > tr > th {
          background: #f8f9fa;
          padding: 12px;
        }
        .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
          padding: 12px;
        }
      `}</style>

      {/* Complete with Photo Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="sm:max-w-md w-[calc(100%-1rem)] max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Terminer la prestation
            </DialogTitle>
            <DialogDescription>
              Ajoutez une photo du travail réalisé pour finaliser cette intervention.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Event info */}
            {selectedEvent && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-semibold">{selectedEvent.title}</p>
                <p className="text-sm text-muted-foreground">
                  {format(selectedEvent.start, 'EEEE d MMMM yyyy', { locale: fr })}
                  {' • '}
                  {format(selectedEvent.start, 'HH:mm')} - {format(selectedEvent.end, 'HH:mm')}
                </p>
                {selectedEvent.collaborator_name && (
                  <p className="text-sm mt-1">
                    <User className="w-3 h-3 inline mr-1" />
                    {selectedEvent.collaborator_name}
                  </p>
                )}
              </div>
            )}
            
            {/* Photo upload */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Photo du chantier terminé
              </Label>
              
              <input
                type="file"
                ref={photoInputRef}
                accept="image/*"
                capture="environment"
                onChange={handlePhotoSelect}
                className="hidden"
              />
              
              {photoPreview ? (
                <div className="relative">
                  <img 
                    src={photoPreview} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setCompletePhoto(null);
                      setPhotoPreview(null);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                  onClick={() => photoInputRef.current?.click()}
                >
                  <Camera className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Cliquez pour prendre ou choisir une photo
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Cette photo sera visible dans l'historique des prestations
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowCompleteDialog(false);
                  setCompletePhoto(null);
                  setPhotoPreview(null);
                }}
              >
                Annuler
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={handleCompleteWithPhoto}
                disabled={isProcessing}
              >
                {isProcessing ? 'En cours...' : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {completePhoto ? 'Terminer avec photo' : 'Terminer sans photo'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Booking Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="sm:max-w-md w-[calc(100%-1rem)] p-4 sm:p-6" data-testid="duplicate-booking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Dupliquer la réservation
            </DialogTitle>
            <DialogDescription>
              Créez une copie de cette réservation à une autre date. Le client, le collaborateur et le créneau sont conservés.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {selectedEvent && (
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="text-sm font-medium">{selectedEvent.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Source : {format(selectedEvent.start, 'EEEE d MMMM yyyy', { locale: fr })}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Nouvelle date</Label>
              <Input
                type="date"
                value={duplicateTargetDate}
                onChange={(e) => setDuplicateTargetDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                data-testid="duplicate-target-date"
              />
            </div>
            <p className="text-xs text-muted-foreground italic">
              💡 Astuce : Alt+clic sur un événement du planning pour ouvrir cette fenêtre directement.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDuplicateDialog(false)}>Annuler</Button>
            <Button onClick={handleDuplicateBooking} disabled={isProcessing || !duplicateTargetDate} data-testid="duplicate-confirm-btn">
              {isProcessing ? 'Duplication…' : 'Dupliquer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Start with Photo Dialog (Before Photo) */}
      <Dialog open={showStartDialog} onOpenChange={setShowStartDialog}>
        <DialogContent className="sm:max-w-md w-[calc(100%-1rem)] max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Démarrer la prestation
            </DialogTitle>
            <DialogDescription>
              Prenez une photo "avant" du chantier pour documenter l'état initial.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Event info */}
            {selectedEvent && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-semibold">{selectedEvent.title}</p>
                <p className="text-sm text-muted-foreground">
                  {format(selectedEvent.start, 'EEEE d MMMM yyyy', { locale: fr })}
                  {' • '}
                  {format(selectedEvent.start, 'HH:mm')} - {format(selectedEvent.end, 'HH:mm')}
                </p>
                {selectedEvent.collaborator_name && (
                  <p className="text-sm mt-1">
                    <User className="w-3 h-3 inline mr-1" />
                    {selectedEvent.collaborator_name}
                  </p>
                )}
              </div>
            )}
            
            {/* Photo upload */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Photo AVANT intervention
              </Label>
              
              <input
                type="file"
                ref={startPhotoInputRef}
                accept="image/*"
                capture="environment"
                onChange={handleStartPhotoSelect}
                className="hidden"
              />
              
              {startPhotoPreview ? (
                <div className="relative">
                  <img 
                    src={startPhotoPreview} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => {
                      setStartPhoto(null);
                      setStartPhotoPreview(null);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  onClick={() => startPhotoInputRef.current?.click()}
                >
                  <Camera className="w-10 h-10 mx-auto text-blue-400 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Cliquez pour prendre une photo "avant"
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Cette photo sera comparée à la photo "après" dans le résumé
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowStartDialog(false);
                  setStartPhoto(null);
                  setStartPhotoPreview(null);
                }}
              >
                Annuler
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleStartWithPhoto}
                disabled={isProcessing}
              >
                {isProcessing ? 'En cours...' : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {startPhoto ? 'Démarrer avec photo' : 'Démarrer sans photo'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Client Creation Dialog */}
      <Dialog open={isQuickClientDialogOpen} onOpenChange={setIsQuickClientDialogOpen}>
        <DialogContent className="max-w-md w-[calc(100%-1rem)] max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Création rapide de client
            </DialogTitle>
            <DialogDescription>
              Créez un nouveau client et sélectionnez-le directement pour votre réservation
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nom complet *</Label>
              <Input
                placeholder="Ex: Jean Dupont"
                value={quickClientForm.name}
                onChange={(e) => setQuickClientForm({ ...quickClientForm, name: e.target.value })}
                data-testid="quick-client-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="Ex: jean.dupont@email.com"
                value={quickClientForm.email}
                onChange={(e) => setQuickClientForm({ ...quickClientForm, email: e.target.value })}
                data-testid="quick-client-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input
                type="tel"
                placeholder="Ex: 06 12 34 56 78"
                value={quickClientForm.phone}
                onChange={(e) => setQuickClientForm({ ...quickClientForm, phone: e.target.value })}
                data-testid="quick-client-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe *</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Mot de passe du client"
                  value={quickClientForm.password}
                  onChange={(e) => setQuickClientForm({ ...quickClientForm, password: e.target.value })}
                  data-testid="quick-client-password"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={generatePassword}
                  title="Générer un mot de passe"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Ce mot de passe permettra au client de se connecter à son espace
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setIsQuickClientDialogOpen(false);
                  setQuickClientForm({ name: '', email: '', phone: '', password: '' });
                }}
              >
                Annuler
              </Button>
              <Button
                className="flex-1"
                onClick={handleQuickClientCreate}
                disabled={isCreatingClient || !quickClientForm.name || !quickClientForm.email || !quickClientForm.password}
                data-testid="quick-client-submit"
              >
                {isCreatingClient ? 'Création...' : 'Créer et sélectionner'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move conflict dialog — opens when the server detects that the target
          collaborator is not available on the requested slot (drag-drop or
          edit). Lists the conflict(s) + next suggestions so the admin can
          pick one in one click, or override and keep the move. */}
      <Dialog open={!!moveConflict} onOpenChange={(open) => { if (!open) setMoveConflict(null); }}>
        <DialogContent className="max-w-lg w-[calc(100%-1rem)] max-h-[90dvh] overflow-y-auto p-4 sm:p-6" data-testid="planning-move-conflict-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              Créneau indisponible
            </DialogTitle>
            <DialogDescription>
              Le collaborateur ciblé n&apos;est pas disponible sur ce créneau. Choisissez une alternative ou déplacez quand même.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(moveConflict?.conflicts || []).map((c, idx) => (
              <div key={idx} className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
                <div className="text-sm font-medium text-amber-900">
                  {c.collaborator_name} indisponible le {' '}
                  {(() => { try { return format(new Date(c.date), 'EEEE d MMMM', { locale: fr }); } catch(e){ return c.date; } })()}
                  {c.start_time ? ` à ${c.start_time}` : ''}
                </div>
                {Array.isArray(c.suggestions) && c.suggestions.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs text-amber-800">Prochaines disponibilités :</p>
                    <div className="flex flex-wrap gap-2">
                      {c.suggestions.map((s, sidx) => (
                        <Button
                          key={sidx}
                          size="sm"
                          variant="outline"
                          className="bg-white"
                          onClick={() => moveConflict?.onApplySuggestion?.(s, c.collaborator_id)}
                          data-testid={`planning-move-suggestion-${c.collaborator_id}-${sidx}`}
                        >
                          {(() => { try { return format(new Date(s.date), 'EEE d MMM', { locale: fr }); } catch(e){ return s.date; } })()}
                          {' '}à {s.start_time}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-amber-800">Aucune disponibilité trouvée sur les 21 prochains jours.</p>
                )}
              </div>
            ))}
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setMoveConflict(null)} data-testid="planning-move-conflict-cancel">
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  setIsProcessing(true);
                  await moveConflict.perform(true);
                  toast.success('Réservation déplacée (créneau forcé)');
                  setMoveConflict(null);
                  fetchData();
                } catch (e) {
                  console.error(e);
                  toast.error("Erreur lors du déplacement forcé");
                } finally {
                  setIsProcessing(false);
                }
              }}
              data-testid="planning-move-conflict-force"
            >
              Déplacer quand même
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
